import { zodResolver } from '@hookform/resolvers/zod';
import { BillingDraftType, customer } from '@server/prisma-generated/client';
import { keepPreviousData } from '@tanstack/react-query';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns'; // For date formatting
import { de } from 'date-fns/locale';
import { Activity, AlertCircle, AlertCircleIcon, AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, ArrowUpDown, Cake, CalendarCheck, CalendarIcon, Calendar as CalendarIconLucide, CheckCircle, CheckCircle2, ChevronDown, ChevronRight, Circle, Clock, DollarSign, Download, ExternalLink, Eye, File, FileText, Gift, History, Link2, Loader2, MapPin, MoreHorizontal, Pencil, Plus, RefreshCw, Save, Trash2, Upload, User, Wallet, X, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '~/components/ui/alert-dialog';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Calendar } from "~/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Progress } from '~/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { Separator } from '~/components/ui/separator';
import { Skeleton } from '~/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from '~/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '~/components/ui/dropdown-menu';
import LocalDateTime from '~/lib/localdatetime';
import { invoiceTypeLabels } from '~/lib/invoice-type-config';
import { paymentMethodConfig, defaultPaymentMethod } from '~/lib/payment-method-config';
import { AutoComplete } from '~/components/ui/autocomplete';
import { cn } from "~/lib/utils"; // Adjust path for Shadcn utils
import { CreateOrModifyAppointmentDrawer } from '~/pages/AppointmentsCalendar';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useUserStore } from '~/stores/user-store';
import { AppointmentStatus } from '~/types/server-types/AppointmentStatus';
import { CustomerCountry } from '~/types/server-types/CustomerCountry';
import { CustomerDocumentType, getCustomerDocumentTypeLabel } from '~/types/server-types/CustomerDocumentType';
import { CustomerGender } from '~/types/server-types/CustomerGender';
import { CustomerTagType } from '~/types/server-types/CustomerTagType';
import { UserRole } from '~/types/server-types/UserRole';
import { ButtonOpenTreatmentPlanner } from '~/components/customer/ButtonOpenTreatmentPlanner';
import { SendAnamneseUrlDialog } from '~/components/customer/SendAnamneseUrlDialog';
import MedicalRecordSection from '~/components/customer/MedicalRecordSection';
import PaymentHistoryDialog from '~/components/customer/PaymentHistoryDialog';
import BundleVouchersDialog from '~/components/customer/BundleVouchersDialog';
import CustomerBundlesTable from '~/components/customer/CustomerBundlesTable';
import CustomerGiftCardsTable from '~/components/customer/CustomerGiftCardsTable';
import { PractitionerNameWithCategory } from '~/components/practitioners/PractitionerNameWithCategory';


// --- TYPEN UND UTILITY-FUNKTIONEN ---

interface FHIRQuestionnaireResponseItem {
    linkId: string;
    definition?: string;
    text?: string;
    answer?: Array<{
        valueBoolean?: boolean;
        valueDecimal?: number;
        valueInteger?: number;
        valueDate?: string;
        valueDateTime?: string;
        valueTime?: string;
        valueString?: string;
        valueUri?: string;
        valueAttachment?: {
            contentType?: string;
            language?: string;
            data?: string;
            url?: string;
            size?: number;
            hash?: string;
            title?: string;
            creation?: string;
        };
        valueCoding?: {
            system?: string;
            version?: string;
            code?: string;
            display?: string;
            userSelected?: boolean;
        };
        valueQuantity?: {
            value?: number;
            comparator?: '<' | '<=' | '>=' | '>';
            unit?: string;
            system?: string;
            code?: string;
        };
        valueReference?: {
            reference?: string;
            type?: string;
            identifier?: {
                use?: string;
                type?: {
                    coding?: Array<{
                        system?: string;
                        code?: string;
                        display?: string;
                    }>;
                    text?: string;
                };
                system?: string;
                value?: string;
                period?: {
                    start?: string;
                    end?: string;
                };
                assigner?: {
                    display?: string;
                };
            };
            display?: string;
        };
        item?: FHIRQuestionnaireResponseItem[];
    }>;
    item?: FHIRQuestionnaireResponseItem[];
}

type FHIRQuestionnaireItem = {
    linkId: string;
    item?: FHIRQuestionnaireItem[];
};

export interface ExpandedFHIRQuestionnaire {
    id: string;
    title: string;
    status: string;
    resourceType: string;
    items: QuestionnaireItem[];
}

export interface QuestionnaireItem {
    linkId: string;
    type: string;
    text?: string;
    required?: boolean;
    repeats?: boolean;
    isCave: boolean;
    items?: QuestionnaireItem[];
    enableWhen?: EnableWhen[];
    answerOption?: AnswerOption[];
}

export interface EnableWhen {
    operator: string;
    question: string;
    answerCoding?: {
        code: string;
    };
}

export interface AnswerOption {
    valueCoding: {
        code: string;
        display: string;
    };
}

export function parseFHIRQuestionnaire(rawQuestionnaire: any): ExpandedFHIRQuestionnaire {
    const extractItems = (items: any[]): QuestionnaireItem[] => {
        return items.map(item => {
            const isCave = item.extension?.some((ext: any) =>
                ext.url === "http://example.org/fhir/StructureDefinition/questionnaire-cave" &&
                ext.valueBoolean === true
            ) || false;

            const parsed: QuestionnaireItem = {
                linkId: item.linkId,
                type: item.type,
                text: item.text,
                required: item.required,
                repeats: item.repeats,
                isCave,
                answerOption: item.answerOption,
                enableWhen: item.enableWhen
            };

            if (item.item && item.item.length > 0) {
                parsed.items = extractItems(item.item);
            }

            return parsed;
        });
    };

    return {
        id: rawQuestionnaire.id,
        title: rawQuestionnaire.title,
        status: rawQuestionnaire.status,
        resourceType: rawQuestionnaire.resourceType,
        items: extractItems(rawQuestionnaire.item || [])
    };
}

export function getCaveItems(questionnaire: ExpandedFHIRQuestionnaire): string[] {
    const caveItems: string[] = [];
    const traverseItems = (items: QuestionnaireItem[]) => {
        items.forEach(item => {
            if (item.isCave) {
                caveItems.push(item.linkId);
            }
            if (item.items) {
                traverseItems(item.items);
            }
        });
    };
    traverseItems(questionnaire.items);
    return caveItems;
}

export function getNonCaveItems(questionnaire: ExpandedFHIRQuestionnaire): string[] {
    const nonCaveItems: string[] = [];
    const traverseItems = (items: QuestionnaireItem[]) => {
        items.forEach(item => {
            if (!item.isCave) {
                nonCaveItems.push(item.linkId);
            }
            if (item.items) {
                traverseItems(item.items);
            }
        });
    };
    traverseItems(questionnaire.items);
    return nonCaveItems;
}

// --- HELPER-KOMPONENTEN FÜR DAS RENDERING DER ANTWORTEN ---

const getAnswerValue = (answer: any) => {
    if (typeof answer.valueBoolean === 'boolean') {
        return answer.valueBoolean ? 'Ja' : 'Nein';
    } else if (typeof answer.valueDecimal === 'number') {
        return answer.valueDecimal;
    } else if (typeof answer.valueInteger === 'number') {
        return answer.valueInteger;
    } else if (typeof answer.valueDate === 'string') {
        return answer.valueDate;
    } else if (typeof answer.valueDateTime === 'string') {
        return answer.valueDateTime;
    } else if (typeof answer.valueTime === 'string') {
        return answer.valueTime;
    } else if (typeof answer.valueString === 'string' && answer.valueString.trim().length > 0) {
        return answer.valueString;
    } else if (typeof answer.valueUri === 'string') {
        return (
            <a href={answer.valueUri} target="_blank" rel="noopener noreferrer" className="underline text-primary break-all">
                {answer.valueUri}
            </a>
        );
    } else if (answer.valueAttachment) {
        return (
            <a href={answer.valueAttachment.url} target="_blank" rel="noopener noreferrer" className="underline text-primary break-all">
                {answer.valueAttachment.title || answer.valueAttachment.url}
            </a>
        );
    } else if (answer.valueCoding) {
        if (Array.isArray(answer.valueCoding)) {
            return answer.valueCoding
                .map((coding: any) => coding.display || coding.code || JSON.stringify(coding))
                .join(', ');
        } else {
            return answer.valueCoding.display || answer.valueCoding.code || JSON.stringify(answer.valueCoding);
        }
    } else if (answer.valueQuantity) {
        return `${answer.valueQuantity.value ?? ''} ${answer.valueQuantity.unit ?? ''}`.trim();
    } else if (answer.valueReference) {
        return answer.valueReference.display || answer.valueReference.reference || JSON.stringify(answer.valueReference);
    } else {
        return null;
    }
};

const getIsAffirmative = (answer: any) => {
    return answer.isBlank !== true || answer.valueBoolean === true || !!answer.valueCoding || !!answer.valueQuantity || !!answer.valueReference || !!answer.valueString || !!answer.valueUri || !!answer.valueAttachment || !!answer.valueDecimal || !!answer.valueInteger || !!answer.valueDate || !!answer.valueDateTime || !!answer.valueTime;
};

// Überarbeitete QuestionGroupDisplay-Komponente
type QuestionGroupDisplayProps = {
    title: string;
    description: string;
    items: { // Question group
        text: string;
        item: { // Question
            text: string;
            answer: any[] | undefined;
        }[] | undefined;
    }[];
    isHealthGroup?: boolean;
}
const QuestionGroupDisplay = ({ title, description, items, isHealthGroup = false }: QuestionGroupDisplayProps) => {
    if (!items || items.length === 0) return null;

    return (
        <Card className={cn("rounded-lg border p-3", isHealthGroup ? "bg-gray-100" : "bg-muted/50")}>
            <CardHeader className="p-0 mb-1">
                <CardTitle className="text-primary text-sm font-semibold">{title}</CardTitle>
                <CardDescription className="text-xs">{description}</CardDescription>
            </CardHeader>
            <CardContent className="p-0 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((questionGroup, idx) => (
                        <div key={idx} className="bg-background rounded-md p-2 shadow-sm border border-muted/60">
                            <div className="font-bold text-xs text-black mb-1">
                                {questionGroup.text}
                            </div>
                            {questionGroup.item?.map((question, aIdx) => (
                                <div key={aIdx} className="grid grid-cols-[1fr,1fr] gap-2 py-1 border-b last:border-b-0">
                                    {!!questionGroup.item && questionGroup.item.length > 2 && (
                                        <span className="font-semibold text-xs min-w-[120px]">{question.text}</span>
                                    )}
                                    <div className="flex flex-col gap-1 text-xs">
                                        {question.answer?.map((answer, answerIdx) => {
                                            const isAffirmative = getIsAffirmative(answer);
                                            const value = getAnswerValue(answer);

                                            if (answer.item && answer.item.length > 0) {
                                                const nestedAnswer = answer.item?.[0]?.answer?.[0];
                                                const nestedValue = nestedAnswer ? getAnswerValue(nestedAnswer) : null;

                                                if (nestedValue) {
                                                    return (
                                                        <span key={answerIdx} className="text-red-500">
                                                            {!isHealthGroup && <span className="mr-1">•</span>}
                                                            {answer.valueString} - {nestedValue}
                                                        </span>
                                                    );
                                                }
                                            }

                                            if (value === null) {
                                                return null;
                                            }

                                            return (
                                                <span key={answerIdx} className={cn(
                                                    "text-black",
                                                    isAffirmative ? "text-red-500 underline" : "",
                                                    isHealthGroup && isAffirmative ? "font-semibold underline text-lg text-red-500" : ""
                                                )}>
                                                    {isHealthGroup && isAffirmative ? <AlertCircle className="inline-block h-3 w-3 mr-1" /> : !isHealthGroup ? <span className="mr-1">•</span> : ''}
                                                    {value}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

// --- HAUPTKOMPONENTE ---

type QuestionnaireResponseModalProps = {
    questionnaireResponseId: number;
    open: boolean;
    onClose: () => void;
};

export const QuestionnaireResponseModal = ({ questionnaireResponseId, open, onClose }: QuestionnaireResponseModalProps) => {
    const { data: questionnaireResponseQuery } = trpc.questionnaireResponses.getQuestionnaireResponse.useQuery(
        { id: questionnaireResponseId },
        { enabled: open, placeholderData: keepPreviousData }
    );

    const response = questionnaireResponseQuery?.data;

    const { healthGroupItems, nonHealthGroupItems } = useMemo(() => {
        if (!response) {
            return { healthGroupItems: [], nonHealthGroupItems: [] };
        }

        const parsedAnamneseItems = typeof response.items === "string" ? JSON.parse(response.items) : response.items;
        const parsedQuestionnaire = parseFHIRQuestionnaire(JSON.parse(response.questionnaire));
        const questionnaireCAVEItemLinkIds = getCaveItems(parsedQuestionnaire);

        const filterItems = (filterFn: any) => {
            return parsedAnamneseItems.map((item: any) => ({
                ...item,
                item: item.item
                    ?.filter((answerItem: { linkId: string }) => filterFn(answerItem.linkId))
                    .map((answerItem: { answer?: any[];[key: string]: any }) => ({
                        ...answerItem,
                        answer: answerItem.answer?.map((answer: any) => {
                            let isBlank = null;
                            if (Object.keys(answer).length === 0) {
                                isBlank = true;
                            }

                            if (answer.valueString !== undefined && (answer.valueString.trim().length === 0 || answer.valueString.trim().toLowerCase() === "nein" || answer.valueString.trim().toLowerCase() === "no")) {
                                isBlank = true;
                            } else if (answer.valueString !== undefined && answer.valueString.trim().length > 0) {
                                isBlank = false;
                            }

                            if (answer.answerDecimal !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerInteger !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerTime !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerDate !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerDateTime !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerQuantity !== undefined) {
                                isBlank = false;
                            }

                            if (answer.valueCoding !== undefined) {
                                isBlank = false;
                            }

                            if (answer.valueBoolean === true) {
                                isBlank = false;
                            } else if (answer.valueBoolean === false && isBlank !== null) {
                                isBlank = true;
                            }

                            return {
                                ...answer,
                                isBlank: isBlank === null ? false : isBlank,
                            }
                        }),
                    })),
            })).filter((item: any) => item.item && item.item.length > 0);
        };

        const healthItems = filterItems((linkId: string) => questionnaireCAVEItemLinkIds.includes(linkId));
        const nonHealthItems = filterItems((linkId: string) => !questionnaireCAVEItemLinkIds.includes(linkId));

        return { healthGroupItems: healthItems, nonHealthGroupItems: nonHealthItems };
    }, [response]);

    if (!response) {
        return (
            <Dialog open={open} onOpenChange={onClose}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Questionnaire Response</DialogTitle></DialogHeader>
                    <div className="p-4">Loading...</div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="flex flex-col w-full max-h-[80vh] h-[80vh] overflow-y-auto">
                <DialogHeader className='flex-col items-start p-6 pb-0'>
                    <DialogTitle className="text-sm font-semibold text-primary">
                        <span className="text-black font-bold">Anamnesebogen</span> <span className="text-xs font-normal text-muted-foreground">#{response.id}</span>
                    </DialogTitle>
                    <div className="flex flex-row flex-wrap gap-2 text-muted-foreground text-xs items-center mt-1">
                        <Badge
                            variant={response.status === "completed" ? "default" : "destructive"}
                            className={cn(
                                "px-2 py-0.5",
                                response.status === "completed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                            )}
                        >
                            {response.status}
                        </Badge>
                        <span>•</span>
                        <span>Erstellt: {response.authored ? new Date(response.authored).toLocaleString("de-DE") : "N/A"}</span>
                        <span>•</span>
                        <span>Aktualisiert: {response.updatedAt ? new Date(response.updatedAt).toLocaleString("de-DE") : "N/A"}</span>
                        {response.deletedAt && (
                            <>
                                <span>•</span>
                                <span className="text-destructive">Gelöscht: {new Date(response.deletedAt).toLocaleString("de-DE")}</span>
                            </>
                        )}
                        <span className="ml-auto text-right">Kunde ID: <span className="font-mono text-base text-black">{response.customerId}</span></span>
                    </div>
                </DialogHeader>
                <div className="w-full h-full flex items-start p-6 pt-0">
                    <div className="w-full">
                        <div className="rounded-2xl border bg-card shadow-sm flex flex-col gap-4 text-xs">
                            <QuestionGroupDisplay
                                title="⚠️ Antworten (CAVE Gesundheits-/Risikofelder)"
                                description="Dies sind die CAVE Gesundheits- und Risikofelder aus dem Fragebogen."
                                items={healthGroupItems}
                                isHealthGroup={true}
                            />
                            <QuestionGroupDisplay
                                title="Weitere Antworten"
                                description="Dies sind die übrigen Antworten aus dem Fragebogen."
                                items={nonHealthGroupItems}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

type QuestionnaireResponseModalButtonProps = {
    questionnaireResponseId: number;
    createdAt: Date;
    size?: 'default' | 'sm';
}
export const QuestionnaireResponseModalButton = ({ questionnaireResponseId, createdAt, size = 'default' }: QuestionnaireResponseModalButtonProps) => {
    const [questionnaireResponseModalOpen, setQuestionnaireResponseModalOpen] = useState(false);

    return (
        <>
            <QuestionnaireResponseModal
                questionnaireResponseId={questionnaireResponseId}
                open={questionnaireResponseModalOpen}
                onClose={() => setQuestionnaireResponseModalOpen(false)}
            />
            {size === 'sm' ? (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuestionnaireResponseModalOpen(true)}
                    title="Anamnese ansehen"
                >
                    <Eye className="h-4 w-4" />
                </Button>
            ) : (
                <div className="flex items-center w-fit gap-3 rounded-md border border-muted px-3 py-2 bg-muted/40">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => setQuestionnaireResponseModalOpen(true)}
                    >
                        <FileText className="h-4 w-4" />
                        <span>Komplette Anamnese anzeigen</span>
                    </Button>
                    <span className="text-xs text-muted-foreground ml-2">
                        {format(createdAt, 'dd.MM.yyyy, HH:mm')}
                    </span>
                </div>
            )}
        </>
    )
}

type ButtonViewBloodReportProps = {
    bloodReportId: string;
};

const ButtonViewBloodReport = ({ bloodReportId }: ButtonViewBloodReportProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const trpcUtils = trpc.useUtils();

    const handleClick = useCallback(async () => {
        setIsLoading(true);
        try {
            const url = await trpcUtils.customers.getBloodReportViewUrl.fetch({ bloodReportId });
            if (!url) {
                toast.error('Kein Link zum Blutbefund verfügbar.');
                setIsLoading(false);
                return;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (error: any) {
            toast.error(error?.message || 'Fehler beim Öffnen des Blutbefunds.');
        } finally {
            setIsLoading(false);
        }
    }, [bloodReportId, trpcUtils]);

    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={handleClick}
            disabled={isLoading}
            title="Blutbefund ansehen"
        >
            {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
                <Eye className="h-4 w-4" />
            )}
        </Button>
    );
}

// --- Blood Report Flow Modal ---
type BloodReportFlowModalProps = {
    bloodReportId: string;
    isOpen: boolean;
    onClose: () => void;
};

type FlowStepKey = 'remiOrder' | 'anivaAppointment' | 'appointment' | 'remiResults' | 'anivaResults' | 'ohUpload' | 'ohReport';

type FlowStep = {
    key: FlowStepKey;
    label: string;
    completed: boolean;
    error: boolean;
    timestamp: Date | null;
    subtitle?: string | null;
};

function computeDocFlowSteps(data: FlowApiData): FlowStep[] {
    const appointmentFailStatuses = new Set(['CANCELLED', 'NO_SHOW', 'CALLED_OFF', 'DECLINED']);
    const appointmentDoneStatuses = new Set(['COMPLETED']);

    const apptStatus = data.appointment?.status;
    const apptCompleted = !!apptStatus && appointmentDoneStatuses.has(apptStatus);
    const apptFailed = !!apptStatus && appointmentFailStatuses.has(apptStatus);
    const apptTimestamp = data.appointment?.dateUntil
        ? new Date(data.appointment.dateUntil)
        : data.appointment?.dateFrom
            ? new Date(data.appointment.dateFrom)
            : null;

    const resultTimestamp = data.resultDate ? new Date(data.resultDate) : null;

    const ohCreatedTimestamp = data.ohCreatedAt ? new Date(data.ohCreatedAt) : null;
    const ohUpdatedTimestamp = data.ohUpdatedAt ? new Date(data.ohUpdatedAt) : null;

    if (data.provider === 'aniva') {
        const anivaStatus = data.anivaAppointmentStatus;
        const anivaBloodDrawn = anivaStatus === 'blood_drawn' || anivaStatus === 'shipment_picked_up';
        const anivaCancelled = anivaStatus === 'cancelled';
        const anivaResultsReady = !!data.resultDate;

        return [
            {
                key: 'anivaAppointment',
                label: 'Aniva Termin',
                completed: anivaBloodDrawn,
                error: anivaCancelled,
                timestamp: anivaBloodDrawn ? new Date(data.createdAt) : null,
                subtitle: anivaCancelled ? '(Storniert)' : null,
            },
            {
                key: 'appointment',
                label: 'Termin',
                completed: apptCompleted,
                error: apptFailed,
                timestamp: (apptCompleted || apptFailed) ? apptTimestamp : null,
            },
            {
                key: 'anivaResults',
                label: 'Aniva Ergebnis',
                completed: anivaResultsReady,
                error: false,
                timestamp: anivaResultsReady ? resultTimestamp : null,
            },
            {
                key: 'ohUpload',
                label: 'OH Upload',
                completed: data.hasDocument,
                error: false,
                timestamp: data.hasDocument ? ohCreatedTimestamp : null,
            },
            {
                key: 'ohReport',
                label: 'OH Bericht',
                completed: data.openHealthStatus === 'READY',
                error: data.openHealthStatus === 'ERROR' || data.openHealthStatus === 'REJECTED',
                timestamp: data.openHealthStatus === 'READY' ? ohUpdatedTimestamp : null,
            },
        ];
    }

    // Remi flow
    const remiResultsReady = data.remiStatus === 'completed';

    return [
        {
            key: 'remiOrder',
            label: 'Remi Erstellt',
            completed: true,
            error: false,
            timestamp: new Date(data.createdAt),
        },
        {
            key: 'appointment',
            label: 'Termin',
            completed: apptCompleted,
            error: apptFailed,
            timestamp: (apptCompleted || apptFailed) ? apptTimestamp : null,
        },
        {
            key: 'remiResults',
            label: 'Remi Ergebnis',
            completed: remiResultsReady,
            error: data.remiStatus === 'cancelled',
            timestamp: remiResultsReady ? resultTimestamp : null,
            subtitle: data.remiStatus === 'cancelled' ? '(Von uns storniert)' : null,
        },
        {
            key: 'ohUpload',
            label: 'OH Upload',
            completed: data.hasDocument,
            error: false,
            timestamp: data.hasDocument ? ohCreatedTimestamp : null,
        },
        {
            key: 'ohReport',
            label: 'OH Bericht',
            completed: data.openHealthStatus === 'READY',
            error: data.openHealthStatus === 'ERROR' || data.openHealthStatus === 'REJECTED',
            timestamp: data.openHealthStatus === 'READY' ? ohUpdatedTimestamp : null,
        },
    ];
}

function flowFormatDuration(ms: number): string {
    if (ms < 0) return '-';
    const totalMinutes = Math.floor(ms / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);
    if (totalDays > 0) {
        const hours = totalHours % 24;
        return hours > 0 ? `${totalDays}T ${hours}Std` : `${totalDays}T`;
    }
    if (totalHours > 0) {
        const minutes = totalMinutes % 60;
        return minutes > 0 ? `${totalHours}Std ${minutes}Min` : `${totalHours}Std`;
    }
    if (totalMinutes > 0) return `${totalMinutes}Min`;
    return '<1Min';
}

const flowStepDialogTitles: Record<FlowStepKey, string> = {
    remiOrder: 'Remi Health – Lab Order',
    anivaAppointment: 'Aniva – Termin',
    appointment: 'Termin – Details',
    remiResults: 'Remi Health – Ergebnisse',
    anivaResults: 'Aniva – Ergebnisse',
    ohUpload: 'OpenHealth – Upload',
    ohReport: 'OpenHealth – Bericht',
};

type FlowApiData = {
    bloodReportId: string;
    createdAt: Date | string;
    provider: 'remi' | 'aniva';
    remiHealthLabOrderId: string | null;
    anivaAppointmentId: string | null;
    anivaAppointmentStatus: string | null;
    anivaBarcode: string | null;
    openHealthFileId: string | null;
    remiStatus: string;
    resultDate: string | null;
    ohCreatedAt: string | null;
    ohUpdatedAt: string | null;
    hasResults: boolean;
    documentId: string | null;
    hasDocument: boolean;
    openHealthStatus: string | null;
    remiHealthMessage: string | null;
    remiHealthTrackingUrl: string | null;
    remiHealthResultsUrl: string | null;
    remiHealthUrl: string | null;
    openHealthMessage: string | null;
    openHealthUrl: string | null;
    panelName: string | null;
    deletedAt: Date | string | null;
    appointment: { id: string; status: string; dateFrom: string | null; dateUntil: string | null } | null;
    events: Array<{ eventType: string; createdAt: Date | string }>;
    remiOrder: Record<string, unknown> | null;
    remiResults: Record<string, unknown> | null;
    remiError: string | null;
    anivaAppointment: Record<string, unknown> | null;
    anivaResults: Record<string, unknown> | null;
    anivaError: string | null;
    openHealthUpload: Record<string, unknown> | null;
    openHealthError: string | null;
};

function getFlowStepContent(stepKey: FlowStepKey, data: FlowApiData): { title: string; content: Record<string, unknown> | null; error: string | null } {
    switch (stepKey) {
        case 'remiOrder':
            return {
                title: flowStepDialogTitles.remiOrder,
                content: data.remiOrder as Record<string, unknown> | null,
                error: data.remiError,
            };
        case 'anivaAppointment':
            return {
                title: flowStepDialogTitles.anivaAppointment,
                content: data.anivaAppointment as Record<string, unknown> | null,
                error: data.anivaError,
            };
        case 'appointment':
            return {
                title: flowStepDialogTitles.appointment,
                content: data.appointment
                    ? { id: data.appointment.id, status: data.appointment.status, dateFrom: data.appointment.dateFrom, dateUntil: data.appointment.dateUntil }
                    : { info: 'Kein Termin verknüpft' },
                error: null,
            };
        case 'remiResults':
            return {
                title: flowStepDialogTitles.remiResults,
                content: data.remiResults as Record<string, unknown> | null,
                error: data.remiError,
            };
        case 'anivaResults':
            return {
                title: flowStepDialogTitles.anivaResults,
                content: data.anivaResults as Record<string, unknown> | null,
                error: data.anivaError,
            };
        case 'ohUpload':
        case 'ohReport':
            return {
                title: flowStepDialogTitles[stepKey],
                content: data.openHealthUpload as Record<string, unknown> | null,
                error: data.openHealthError,
            };
        default:
            return { title: '', content: null, error: null };
    }
}

const renderApiValue = (value: unknown, depth = 0): React.ReactNode => {
    if (value === null || value === undefined) return <span className="text-muted-foreground italic">null</span>;
    if (typeof value === 'boolean') return <span className={value ? 'text-green-600' : 'text-red-600'}>{String(value)}</span>;
    if (typeof value === 'number') return <span className="text-blue-600">{value}</span>;
    if (typeof value === 'string') {
        if (value.length > 200) return <span className="text-sm break-all">{value.slice(0, 200)}...</span>;
        return <span className="text-sm break-all">{value}</span>;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) return <span className="text-muted-foreground italic">[]</span>;
        return (
            <div className="pl-3 border-l border-muted">
                {value.map((item, i) => (
                    <div key={i} className="py-0.5">
                        <span className="text-muted-foreground text-xs mr-1">[{i}]</span>
                        {renderApiValue(item, depth + 1)}
                    </div>
                ))}
            </div>
        );
    }
    if (typeof value === 'object') {
        return (
            <div className={cn(depth > 0 && 'pl-3 border-l border-muted')}>
                {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
                    <div key={k} className="py-0.5 flex gap-2">
                        <span className="text-xs font-mono text-muted-foreground shrink-0">{k}:</span>
                        <div className="min-w-0">{renderApiValue(v, depth + 1)}</div>
                    </div>
                ))}
            </div>
        );
    }
    return <span>{String(value)}</span>;
};

const BloodReportFlowModal = ({ bloodReportId, isOpen, onClose }: BloodReportFlowModalProps) => {
    const trpcUtils = trpc.useUtils();
    const { data, isLoading } = trpc.customers.getBloodReportFlow.useQuery(
        { bloodReportId },
        { enabled: isOpen },
    );
    const [activeStepKey, setActiveStepKey] = useState<FlowStepKey | null>(null);

    if (!isOpen) return null;

    const steps = data ? computeDocFlowSteps(data) : [];
    const currentStepIndex = steps.findIndex(s => !s.completed && !s.error);
    const now = Date.now();

    const activeStepContent = activeStepKey && data ? getFlowStepContent(activeStepKey, data) : null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { onClose(); setActiveStepKey(null); } }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>Blutbefund Status</DialogTitle>
                </DialogHeader>

                {isLoading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                )}

                {!isLoading && !data && (
                    <div className="text-center py-8 text-muted-foreground">
                        Kein verknüpfter Blutbefund gefunden.
                    </div>
                )}

                {!isLoading && data && (
                    <div className="flex-1 overflow-y-auto space-y-5 pr-1">
                        {/* Flow Progress with durations */}
                        <div className="flex items-start w-full px-2 py-2">
                            {steps.map((step, i) => {
                                const isCompleted = step.completed;
                                const isError = step.error;
                                const isCurrent = i === currentStepIndex;
                                const isFuture = !isCompleted && !isError && !isCurrent;

                                return (
                                    <React.Fragment key={i}>
                                        <div className="flex flex-col items-center" style={{ minWidth: '72px' }}>
                                            <button
                                                type="button"
                                                onClick={() => setActiveStepKey(activeStepKey === step.key ? null : step.key)}
                                                className={cn(
                                                    'w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-primary/40',
                                                    isCompleted && 'bg-green-500 border-green-500 text-white',
                                                    isError && 'bg-red-100 border-red-500 text-red-600',
                                                    isCurrent && 'bg-amber-50 border-amber-500 text-amber-600 animate-pulse',
                                                    isFuture && 'bg-muted border-muted-foreground/30 text-muted-foreground/50',
                                                    activeStepKey === step.key && 'ring-2 ring-offset-1 ring-primary',
                                                )}
                                                title={`${step.label} – Klicken für Details`}
                                            >
                                                {isCompleted && <CheckCircle2 className="h-4 w-4" />}
                                                {isError && <XCircle className="h-4 w-4" />}
                                                {isCurrent && <Clock className="h-4 w-4" />}
                                                {isFuture && <Circle className="h-4 w-4" />}
                                            </button>
                                            <span className="text-xs font-medium mt-1.5 text-center leading-tight">
                                                {step.label}
                                            </span>
                                            {step.subtitle && (
                                                <span className="text-[10px] text-red-500 text-center leading-tight">
                                                    {step.subtitle}
                                                </span>
                                            )}
                                            {step.timestamp && (
                                                <span className="text-[10px] text-muted-foreground">
                                                    {format(step.timestamp, 'dd.MM.yy', { locale: de })}
                                                </span>
                                            )}
                                        </div>

                                        {i < steps.length - 1 && (() => {
                                            const nextStep = steps[i + 1];
                                            const segmentCompleted = isCompleted && (nextStep.completed || nextStep.error);
                                            const isWaiting = isCompleted && !nextStep.completed && !nextStep.error;

                                            let durationText: string | null = null;
                                            if (segmentCompleted && step.timestamp && nextStep.timestamp) {
                                                durationText = flowFormatDuration(nextStep.timestamp.getTime() - step.timestamp.getTime());
                                            } else if (isWaiting && step.timestamp) {
                                                durationText = flowFormatDuration(now - step.timestamp.getTime());
                                            }

                                            return (
                                                <div className="flex-1 flex flex-col items-center pt-3.5 min-w-[30px]">
                                                    <div
                                                        className={cn(
                                                            'w-full h-0.5',
                                                            segmentCompleted && 'bg-green-500',
                                                            isWaiting && 'border-t-2 border-dashed border-amber-400',
                                                            !segmentCompleted && !isWaiting && 'bg-muted-foreground/20',
                                                        )}
                                                    />
                                                    {durationText && (
                                                        <span className={cn(
                                                            'text-[10px] mt-1 whitespace-nowrap',
                                                            isWaiting ? 'text-amber-600 font-medium' : 'text-muted-foreground',
                                                        )}>
                                                            {durationText}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Step detail panel (shown when a step is clicked) */}
                        {activeStepContent && (
                            <>
                                <Separator />
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold">{activeStepContent.title}</h4>
                                    {activeStepContent.error && (
                                        <div className="text-sm text-red-600 py-2 px-3 bg-red-50 rounded-md">
                                            API-Fehler: {activeStepContent.error}
                                        </div>
                                    )}
                                    {activeStepContent.content ? (
                                        <div className="text-sm font-mono">
                                            {renderApiValue(activeStepContent.content)}
                                        </div>
                                    ) : !activeStepContent.error && (
                                        <div className="text-sm text-muted-foreground text-center py-4">
                                            Keine Daten verfügbar für diesen Schritt.
                                        </div>
                                    )}

                                    {/* Action buttons below content */}
                                    {activeStepKey === 'remiResults' && data.documentId && (
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="px-0 h-auto text-xs"
                                            onClick={async () => {
                                                try {
                                                    const result = await trpcUtils.customers.getDocumentReadUrl.fetch({ documentId: data.documentId! });
                                                    if (result?.downloadUrl) window.open(result.downloadUrl, '_blank');
                                                } catch { toast.error('Fehler beim Laden des Dokuments.'); }
                                            }}
                                        >
                                            <ExternalLink className="h-3 w-3 mr-1" />
                                            Ergebnis-PDF ansehen
                                        </Button>
                                    )}
                                    {(activeStepKey === 'ohUpload' || activeStepKey === 'ohReport') && data.openHealthStatus === 'READY' && data.bloodReportId && (
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="px-0 h-auto text-xs"
                                            onClick={async () => {
                                                try {
                                                    const url = await trpcUtils.customers.getBloodReportViewUrl.fetch({ bloodReportId: data.bloodReportId });
                                                    if (url) window.open(url, '_blank');
                                                    else toast.error('Kein Link zum Bericht verfügbar.');
                                                } catch { toast.error('Fehler beim Öffnen des Berichts.'); }
                                            }}
                                        >
                                            <ExternalLink className="h-3 w-3 mr-1" />
                                            Bericht ansehen
                                        </Button>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Events (always visible at bottom if any exist) */}
                        {!activeStepKey && data.events.length > 0 && (
                            <>
                                <Separator />
                                <div>
                                    <h4 className="text-sm font-semibold mb-2">Webhook Events ({data.events.length})</h4>
                                    <div className="space-y-1">
                                        {data.events.map((evt, i) => (
                                            <div key={i} className="flex justify-between text-xs py-1">
                                                <span className="font-mono">{evt.eventType}</span>
                                                <span className="text-muted-foreground">
                                                    {format(new Date(evt.createdAt), 'dd.MM.yy HH:mm', { locale: de })}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

// --- Lab Order / Blood Sample Types ---
type LabOrdersListProps = {
    customerId: number;
};

// Map blood sample event types to display values
const eventTypeMap: Record<string, { label: string; color: string; bgColor: string }> = {
    ORDER_SHIPPED: { label: "Versendet", color: "text-blue-700", bgColor: "bg-blue-100" },
    ORDER_DELIVERED: { label: "Zugestellt", color: "text-green-700", bgColor: "bg-green-100" },
    SAMPLE_ARRIVED: { label: "Probe angekommen", color: "text-purple-700", bgColor: "bg-purple-100" },
    RESULTS_AVAILABLE: { label: "Ergebnisse verfügbar", color: "text-emerald-700", bgColor: "bg-emerald-100" },
    RESULTS_NOT_MEASURABLE: { label: "Nicht messbar", color: "text-orange-700", bgColor: "bg-orange-100" },
};

type BloodSampleEvent = {
    id: string;
    createdAt: string | Date;
    webhookId: string;
    eventType: string;
    trackingUrl: string | null;
    resultUrl: string | null;
    parameters: unknown;
    rawData: unknown;
};

type BloodSampleEventsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    events: BloodSampleEvent[];
    remiHealthLabOrderId: string | null;
};

const BloodSampleEventsModal = ({ isOpen, onClose, events, remiHealthLabOrderId }: BloodSampleEventsModalProps) => {
    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <History className="h-5 w-5" />
                        Ereignisse für Labor-Auftrag
                        {remiHealthLabOrderId && <span className="font-mono text-sm text-muted-foreground">({remiHealthLabOrderId})</span>}
                    </DialogTitle>
                </DialogHeader>

                {events.length === 0 ? (
                    <div className="text-muted-foreground text-sm py-8 text-center">
                        Keine Ereignisse vorhanden.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {events.map((event) => {
                            const eventTypeInfo = eventTypeMap[event.eventType] || {
                                label: event.eventType,
                                color: "text-gray-700",
                                bgColor: "bg-gray-100",
                            };

                            return (
                                <div key={event.id} className="border rounded-lg p-4 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Badge className={`${eventTypeInfo.bgColor} ${eventTypeInfo.color} border-0`}>
                                            {eventTypeInfo.label}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(event.createdAt).toLocaleDateString("de-DE", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                    </div>

                                    <div className="text-xs text-muted-foreground font-mono">
                                        Webhook ID: {event.webhookId}
                                    </div>

                                    {event.trackingUrl && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-muted-foreground">Tracking:</span>
                                            <a
                                                href={event.trackingUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary hover:underline flex items-center gap-1"
                                            >
                                                Link öffnen <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </div>
                                    )}

                                    {event.resultUrl && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-muted-foreground">Ergebnisse:</span>
                                            <a
                                                href={event.resultUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary hover:underline flex items-center gap-1"
                                            >
                                                Link öffnen <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </div>
                                    )}

                                    {event.parameters !== null && event.parameters !== undefined && (
                                        <div className="text-sm">
                                            <span className="text-muted-foreground block mb-1">Parameter (nicht messbar):</span>
                                            <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
                                                {JSON.stringify(event.parameters as Record<string, unknown>, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

const LabOrdersList = ({ customerId }: LabOrdersListProps) => {
    const { data: labOrders, isLoading, isFetching, isError, refetch } = trpc.customers.listLabOrders.useQuery(
        { customerId },
        {
            refetchInterval: 30000,
            refetchIntervalInBackground: false,
        }
    );
    const trpcUtils = trpc.useUtils();
    const [eventsModalOpen, setEventsModalOpen] = useState(false);
    const [selectedOrderEvents, setSelectedOrderEvents] = useState<BloodSampleEvent[]>([]);
    const [selectedLabOrderId, setSelectedLabOrderId] = useState<string | null>(null);

    // Edit activation ID state
    const [editActivationIdModalOpen, setEditActivationIdModalOpen] = useState(false);
    const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
    const [selectedNewActivationIdId, setSelectedNewActivationIdId] = useState<string>('');

    // Expanded items state
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

    // Fetch available activation IDs for editing
    const { data: availableActivationIds, isLoading: isLoadingActivationIds, refetch: refetchActivationIds } = trpc.bloodTests.listActivationIds.useQuery({
        status: 'AVAILABLE',
        pageSize: 100,
    });

    // Filter activation IDs by the editing order's appointment store
    const editingOrder = labOrders?.find((o) => o.id === editingOrderId);
    const filteredEditActivationIds = useMemo(() => {
        if (!availableActivationIds?.items) return [];
        const appointmentStoreId = editingOrder?.appointment?.store?.id;
        if (!appointmentStoreId) return availableActivationIds.items;
        return availableActivationIds.items.filter(
            (a) => !a.store || a.store.id === appointmentStoreId
        );
    }, [availableActivationIds, editingOrder?.appointment?.store?.id]);

    const cancelLabOrderMutation = trpc.customers.cancelLabOrder.useMutation({
        onSuccess: () => {
            toast.success('Labor-Auftrag erfolgreich storniert (Aktivierungs-ID wurde freigegeben)');
            trpcUtils.customers.listLabOrders.invalidate({ customerId });
            refetchActivationIds();
        },
        onError: (err) => {
            toast.error(err.message || 'Fehler beim Stornieren des Labor-Auftrags');
        },
    });

    const updateActivationIdMutation = trpc.customers.updateLabOrderActivationId.useMutation({
        onSuccess: () => {
            toast.success('Aktivierungs-ID erfolgreich geändert');
            trpcUtils.customers.listLabOrders.invalidate({ customerId });
            refetchActivationIds();
            setEditActivationIdModalOpen(false);
            setEditingOrderId(null);
            setSelectedNewActivationIdId('');
        },
        onError: (err) => {
            toast.error(err.message || 'Fehler beim Ändern der Aktivierungs-ID');
        },
    });

    const handleCancelOrder = (bloodReportId: string) => {
        cancelLabOrderMutation.mutate({ bloodReportId });
    };

    const handleOpenEventsModal = (events: BloodSampleEvent[], remiHealthLabOrderId: string | null) => {
        setSelectedOrderEvents(events);
        setSelectedLabOrderId(remiHealthLabOrderId);
        setEventsModalOpen(true);
    };

    const handleOpenEditActivationIdModal = (orderId: string) => {
        setEditingOrderId(orderId);
        setSelectedNewActivationIdId('');
        setEditActivationIdModalOpen(true);
    };

    const handleUpdateActivationId = () => {
        if (!editingOrderId || !selectedNewActivationIdId) return;
        updateActivationIdMutation.mutate({
            bloodReportId: editingOrderId,
            newActivationIdId: selectedNewActivationIdId,
        });
    };

    const toggleExpanded = (orderId: string) => {
        setExpandedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(orderId)) {
                newSet.delete(orderId);
            } else {
                newSet.add(orderId);
            }
            return newSet;
        });
    };

    const formatAppointmentDate = (dateLocalSerialized: string | null) => {
        if (!dateLocalSerialized) return '';
        try {
            const localDateTime = LocalDateTime.deserialize(dateLocalSerialized);
            return localDateTime.getDisplayDateTime();
        } catch {
            return '';
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
            </div>
        );
    }

    if (isError) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Fehler</AlertTitle>
                <AlertDescription>
                    Die Labor-Aufträge konnten nicht geladen werden.
                </AlertDescription>
                <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
                    <RefreshCw className="mr-2 h-4 w-4" /> Erneut versuchen
                </Button>
            </Alert>
        );
    }

    if (!labOrders || labOrders.length === 0) {
        return (
            <div className="text-muted-foreground text-sm py-4">
                Keine Labor-Aufträge vorhanden.
            </div>
        );
    }

    // Map Remi Health status to display values
    const remiStatusMap: Record<string, { label: string; color: string; bgColor: string }> = {
        pending: { label: "Ausstehend", color: "text-yellow-700", bgColor: "bg-yellow-100" },
        processing: { label: "In Bearbeitung", color: "text-blue-700", bgColor: "bg-blue-100" },
        completed: { label: "Abgeschlossen", color: "text-green-700", bgColor: "bg-green-100" },
        cancelled: { label: "Storniert", color: "text-gray-700", bgColor: "bg-gray-100" },
    };

    // Map gender codes to display values
    const genderMap: Record<string, string> = {
        f: "Weiblich",
        m: "Männlich",
        d: "Divers",
    };

    return (
        <>
            <BloodSampleEventsModal
                isOpen={eventsModalOpen}
                onClose={() => setEventsModalOpen(false)}
                events={selectedOrderEvents}
                remiHealthLabOrderId={selectedLabOrderId}
            />

            {/* Edit Activation ID Modal */}
            <Dialog open={editActivationIdModalOpen} onOpenChange={(open) => !open && setEditActivationIdModalOpen(false)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Aktivierungs-ID ändern</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Wählen Sie eine neue Aktivierungs-ID für diesen Labor-Auftrag. Die alte Aktivierungs-ID wird wieder freigegeben.
                        </p>
                        <div className="space-y-2">
                            <Label htmlFor="new-activation-id">Neue Aktivierungs-ID</Label>
                            <Select
                                value={selectedNewActivationIdId}
                                onValueChange={setSelectedNewActivationIdId}
                                disabled={isLoadingActivationIds}
                            >
                                <SelectTrigger id="new-activation-id">
                                    <SelectValue placeholder={isLoadingActivationIds ? "Laden..." : "Aktivierungs-ID auswählen..."} />
                                </SelectTrigger>
                                <SelectContent>
                                    {filteredEditActivationIds.map((activationId) => (
                                        <SelectItem key={activationId.id} value={activationId.id}>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono">{activationId.activationCode}</span>
                                                {activationId.store && (
                                                    <span className="text-xs text-muted-foreground">
                                                        ({activationId.store.name})
                                                    </span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setEditActivationIdModalOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                onClick={handleUpdateActivationId}
                                disabled={!selectedNewActivationIdId || updateActivationIdMutation.isPending}
                            >
                                {updateActivationIdMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : null}
                                Speichern
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                title="Aktualisieren"
                disabled={isFetching}
            >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
            <div className="w-full space-y-4">
                {[...labOrders].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((order) => {
                    const remiData = order.remiData;
                    const remiStatus = remiData?.status ? remiStatusMap[remiData.status] : null;
                    const canCancel = remiData?.status === 'pending';
                    const canEdit = remiData?.status === 'pending';
                    const hasEvents = order.events && order.events.length > 0;
                    const isExpanded = expandedItems.has(order.id);

                    return (
                        <div key={order.id} className="border rounded-lg p-4 space-y-3">
                            {/* Collapsed Header - Always visible */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => toggleExpanded(order.id)}
                                        title={isExpanded ? "Einklappen" : "Ausklappen"}
                                    >
                                        {isExpanded ? (
                                            <ChevronDown className="h-4 w-4" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <span className="font-mono text-sm font-medium">{order.activationIdEntry?.activationCode || order.remiHealthLabOrderId}</span>
                                    {order.panel && (
                                        <span className="text-xs text-muted-foreground font-mono">
                                            ({order.panel.remiPanelId})
                                        </span>
                                    )}
                                    {remiData?.sampleDate && (
                                        <span className="text-xs text-muted-foreground">
                                            Probe: {remiData.sampleDate}
                                        </span>
                                    )}
                                    {remiStatus && (
                                        <Badge className={`${remiStatus.bgColor} ${remiStatus.color} border-0`}>
                                            {remiStatus.label}
                                        </Badge>
                                    )}
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleExpanded(order.id)}
                                    title={isExpanded ? "Einklappen" : "Details anzeigen"}
                                >
                                    <Eye className="h-4 w-4" />
                                </Button>
                            </div>

                            {/* Expanded Content */}
                            {isExpanded && (
                                <>
                                    {/* Header with Lab Order ID and Status */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleOpenEventsModal(order.events as BloodSampleEvent[], order.remiHealthLabOrderId)}
                                                title="Ereignisse anzeigen"
                                            >
                                                Ereignisverlauf anzeigen
                                            </Button>
                                            {canCancel && (
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            disabled={cancelLabOrderMutation.isPending}
                                                        >
                                                            {cancelLabOrderMutation.isPending ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <X className="h-4 w-4 mr-1" />
                                                                    Stornieren
                                                                </>
                                                            )}
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Labor-Auftrag stornieren?</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                Sind Sie sicher, dass Sie diesen Labor-Auftrag stornieren möchten?
                                                                Die Aktivierungs-ID wird freigegeben und kann wiederverwendet werden.
                                                                Diese Aktion kann nicht rückgängig gemacht werden.
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() => handleCancelOrder(order.id)}
                                                                className="bg-destructive text-white hover:bg-destructive/90"
                                                            >
                                                                Stornieren
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            )}
                                        </div>
                                    </div>

                                    {/* Linked Appointment */}
                                    {order.appointment && (
                                        <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-md p-2">
                                            <Link2 className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-muted-foreground">Verknüpfter Termin:</span>
                                            <span className="font-medium">{order.appointment.displayName}</span>
                                            {order.appointment.dateFromLocal && (
                                                <span className="text-muted-foreground">
                                                    ({formatAppointmentDate(order.appointment.dateFromLocal)})
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Panel & Tube Info with Handling Instructions */}
                                    {order.panel && (
                                        <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="font-medium">{order.panel.name}</span>
                                                    <span className="text-xs text-muted-foreground ml-2 font-mono">
                                                        ({order.panel.remiPanelId})
                                                    </span>
                                                </div>
                                                {order.isAutoCreated && (
                                                    <Badge variant="outline" className="text-xs">Auto-erstellt</Badge>
                                                )}
                                            </div>

                                            {/* Panel Description */}
                                            {order.panel.description && (
                                                <p className="text-xs text-muted-foreground">
                                                    {order.panel.description}
                                                </p>
                                            )}

                                            {/* Tube Summary - Updated styling to match LabOrderCreator */}
                                            <div className="flex flex-wrap gap-3 text-sm pb-3 border-b">
                                                {order.panel.edtaTubeCount > 0 && (
                                                    <div className="flex items-center gap-1.5 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded">
                                                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                                                        <span className="font-medium">{order.panel.edtaTubeCount}x EDTA (rot)</span>
                                                    </div>
                                                )}
                                                {order.panel.serumTubeCount > 0 && (
                                                    <div className="flex items-center gap-1.5 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded">
                                                        <span className="w-3 h-3 rounded-full bg-amber-800"></span>
                                                        <span className="font-medium">{order.panel.serumTubeCount}x Serum (braun)</span>
                                                    </div>
                                                )}
                                                {order.panel.fluoridTubeCount > 0 && (
                                                    <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-900/30 px-2 py-1 rounded">
                                                        <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                                                        <span className="font-medium">{order.panel.fluoridTubeCount}x GlucoEXACT (grau)</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Detailed Handling Instructions */}
                                            {order.panel.handlingInstructions && (
                                                <div className="space-y-3">
                                                    <h5 className="text-sm font-semibold">Handhabungsanweisungen</h5>
                                                    {order.panel.edtaTubeCount > 0 && (order.panel.handlingInstructions as { edta?: { centrifuge: boolean; storage: string; notes: string } })?.edta && (
                                                        <div className="border-l-4 border-red-500 pl-3 py-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="font-medium text-sm">EDTA (rot)</span>
                                                                <Badge variant="outline" className="text-xs">
                                                                    {(order.panel.handlingInstructions as { edta?: { centrifuge: boolean } })?.edta?.centrifuge ? 'Zentrifugieren' : 'Nicht zentrifugieren'}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground">
                                                                <span className="font-medium">Lagerung:</span> {(order.panel.handlingInstructions as { edta?: { storage: string } })?.edta?.storage}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                {(order.panel.handlingInstructions as { edta?: { notes: string } })?.edta?.notes}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {order.panel.serumTubeCount > 0 && (order.panel.handlingInstructions as { serum?: { centrifuge: boolean; centrifugeSettings: string; storage: string; notes: string } })?.serum && (
                                                        <div className="border-l-4 border-amber-800 pl-3 py-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="font-medium text-sm">Serum (braun)</span>
                                                                <Badge variant="outline" className="text-xs">
                                                                    {(order.panel.handlingInstructions as { serum?: { centrifuge: boolean } })?.serum?.centrifuge ? 'Zentrifugieren' : 'Nicht zentrifugieren'}
                                                                </Badge>
                                                            </div>
                                                            {(order.panel.handlingInstructions as { serum?: { centrifugeSettings: string } })?.serum?.centrifugeSettings && (
                                                                <p className="text-xs text-muted-foreground">
                                                                    <span className="font-medium">Zentrifuge:</span> {(order.panel.handlingInstructions as { serum?: { centrifugeSettings: string } })?.serum?.centrifugeSettings}
                                                                </p>
                                                            )}
                                                            <p className="text-xs text-muted-foreground">
                                                                <span className="font-medium">Lagerung:</span> {(order.panel.handlingInstructions as { serum?: { storage: string } })?.serum?.storage}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                {(order.panel.handlingInstructions as { serum?: { notes: string } })?.serum?.notes}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {order.panel.fluoridTubeCount > 0 && (order.panel.handlingInstructions as { fluorid?: { centrifuge: boolean; centrifugeSettings: string; storage: string; notes: string } })?.fluorid && (
                                                        <div className="border-l-4 border-gray-500 pl-3 py-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="font-medium text-sm">GlucoEXACT (grau)</span>
                                                                <Badge variant="outline" className="text-xs">
                                                                    {(order.panel.handlingInstructions as { fluorid?: { centrifuge: boolean } })?.fluorid?.centrifuge ? 'Zentrifugieren' : 'Nicht zentrifugieren'}
                                                                </Badge>
                                                            </div>
                                                            {(order.panel.handlingInstructions as { fluorid?: { centrifugeSettings: string } })?.fluorid?.centrifugeSettings && (
                                                                <p className="text-xs text-muted-foreground">
                                                                    <span className="font-medium">Zentrifuge:</span> {(order.panel.handlingInstructions as { fluorid?: { centrifugeSettings: string } })?.fluorid?.centrifugeSettings}
                                                                </p>
                                                            )}
                                                            <p className="text-xs text-muted-foreground">
                                                                <span className="font-medium">Lagerung:</span> {(order.panel.handlingInstructions as { fluorid?: { storage: string } })?.fluorid?.storage}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                {(order.panel.handlingInstructions as { fluorid?: { notes: string } })?.fluorid?.notes}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Activation ID & Store Info with Edit Button */}
                                    {order.activationIdEntry && (
                                        <div className="flex items-center gap-4 text-sm">
                                            <div className="flex items-center gap-2">
                                                <span className="text-muted-foreground">Aktivierungs-ID:</span>
                                                <span className="font-mono font-medium">{order.activationIdEntry.activationCode}</span>
                                                {canEdit && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 w-6 p-0"
                                                        onClick={() => handleOpenEditActivationIdModal(order.id)}
                                                        title="Aktivierungs-ID ändern"
                                                    >
                                                        <Pencil className="h-3 w-3" />
                                                    </Button>
                                                )}
                                            </div>
                                            {(order.activationIdEntry.store || order.appointment?.store) && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-muted-foreground">Standort:</span>
                                                    <span>{order.activationIdEntry.store?.name || order.appointment?.store?.name}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Details from Remi Health API */}
                                    {remiData ? (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                            <div>
                                                <span className="text-muted-foreground block">Patient</span>
                                                <span>{remiData.firstname} {remiData.lastname}</span>
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground block">Geburtsdatum</span>
                                                <span>{remiData.dateOfBirth}</span>
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground block">Geschlecht</span>
                                                <span>{genderMap[remiData.gender] || remiData.gender}</span>
                                            </div>
                                            {remiData.sampleDate && (
                                                <div>
                                                    <span className="text-muted-foreground block">Probenentnahme</span>
                                                    <span>{remiData.sampleDate}</span>
                                                </div>
                                            )}
                                            <div>
                                                <span className="text-muted-foreground block">Erstellt am</span>
                                                <span>
                                                    {new Date(order.createdAt).toLocaleDateString("de-DE", {
                                                        year: "numeric",
                                                        month: "2-digit",
                                                        day: "2-digit",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-muted-foreground text-sm">
                                            Details konnten nicht geladen werden.
                                        </div>
                                    )}

                                    {/* Tracking & Result URLs */}
                                    {(order.remiHealthTrackingUrl || order.remiHealthResultsUrl) && (
                                        <div className="flex flex-wrap gap-4 text-sm pt-2 border-t">
                                            {order.remiHealthTrackingUrl && (
                                                <a
                                                    href={order.remiHealthTrackingUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline flex items-center gap-1"
                                                >
                                                    Sendungsverfolgung <ExternalLink className="h-3 w-3" />
                                                </a>
                                            )}
                                            {order.remiHealthResultsUrl && (
                                                <a
                                                    href={order.remiHealthResultsUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline flex items-center gap-1"
                                                >
                                                    Ergebnisse ansehen <ExternalLink className="h-3 w-3" />
                                                </a>
                                            )}
                                        </div>
                                    )}

                                    {/* Analysis/Panel Info */}
                                    {remiData?.analysis && remiData.analysis.length > 0 && (
                                        <div className="pt-2 border-t">
                                            <span className="text-muted-foreground text-sm block mb-2">Analysen:</span>
                                            <div className="flex flex-wrap gap-2">
                                                {remiData.analysis.map((item, idx) => (
                                                    <Badge key={idx} variant="outline">
                                                        {item.type === 'panel' ? '📋' : '🔬'} {item.id || `Analyse ${idx + 1}`}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

// ── Aniva Health Components ─────────────────────────────────────────────

const AnivaLabOrderCreator = ({ customerId }: { customerId: number }) => {
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>('');
    const [selectedPanelIds, setSelectedPanelIds] = useState<Set<string>>(new Set());
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { data: appointments, isLoading: isLoadingAppointments } = trpc.customers.getAppointmentsForLabOrder.useQuery({ customerId });
    const { data: panels } = trpc.bloodTests.listPanels.useQuery();

    const anivaPanels = useMemo(
        () => (panels ?? []).filter((p) => !!p.anivaPanelId),
        [panels],
    );

    const handleAppointmentChange = useCallback((appointmentId: string) => {
        setSelectedAppointmentId(appointmentId);

        if (!appointmentId || appointmentId === 'NONE') {
            setSelectedPanelIds(new Set());
            return;
        }

        const appointment = appointments?.find(a => a.id === appointmentId);
        if (!appointment) {
            setSelectedPanelIds(new Set());
            return;
        }

        const variantIds = new Set(appointment.treatmentVariantIds);
        const matched = new Set<string>();
        for (const panel of anivaPanels) {
            if (panel.treatmentVariants.some(tv => variantIds.has(tv.id))) {
                matched.add(panel.anivaPanelId!);
            }
        }
        setSelectedPanelIds(matched);
    }, [appointments, anivaPanels]);

    const addPanel = useCallback((anivaPanelId: string) => {
        setSelectedPanelIds(prev => new Set([...prev, anivaPanelId]));
    }, []);

    const removePanel = useCallback((anivaPanelId: string) => {
        setSelectedPanelIds(prev => {
            const next = new Set(prev);
            next.delete(anivaPanelId);
            return next;
        });
    }, []);

    const availablePanelsToAdd = useMemo(
        () => anivaPanels.filter(p => !selectedPanelIds.has(p.anivaPanelId!)),
        [anivaPanels, selectedPanelIds],
    );

    const selectedPanelDetails = useMemo(
        () => anivaPanels.filter(p => selectedPanelIds.has(p.anivaPanelId!)),
        [anivaPanels, selectedPanelIds],
    );

    const trpcUtils = trpc.useUtils();
    const createMutation = trpc.customers.createAnivaLabOrder.useMutation({
        onSuccess: () => {
            toast.success('Aniva Labor-Auftrag erfolgreich erstellt');
            setSelectedAppointmentId('');
            setSelectedPanelIds(new Set());
            setError(null);
            trpcUtils.customers.listAnivaLabOrders.invalidate({ customerId });
        },
        onError: (err) => {
            setError(err.message || 'Fehler beim Erstellen des Aniva Labor-Auftrags');
            toast.error('Fehler beim Erstellen des Aniva Labor-Auftrags');
        },
    });

    const handleCreate = async () => {
        if (!selectedAppointmentId || selectedAppointmentId === 'NONE') {
            setError('Bitte wählen Sie einen Termin aus');
            return;
        }
        if (selectedPanelIds.size === 0) {
            setError('Bitte wählen Sie mindestens ein Panel aus');
            return;
        }
        setError(null);
        setIsCreating(true);
        try {
            await createMutation.mutateAsync({
                customerId,
                appointmentId: selectedAppointmentId,
                anivaPanelIds: Array.from(selectedPanelIds),
            });
        } finally {
            setIsCreating(false);
        }
    };

    const formatAppointmentDate = (dateLocalSerialized: string | null) => {
        if (!dateLocalSerialized) return '';
        try {
            return LocalDateTime.deserialize(dateLocalSerialized).getDisplayDateTime();
        } catch { return ''; }
    };

    const hasAppointment = !!selectedAppointmentId && selectedAppointmentId !== 'NONE';

    return (
        <div className="border rounded-lg p-4 space-y-4">
            <h4 className="font-medium">Neuen Aniva Labor-Auftrag erstellen</h4>
            <div className="space-y-2">
                <Label htmlFor="aniva-appointment-select">Termin verknüpfen</Label>
                <Select value={selectedAppointmentId} onValueChange={handleAppointmentChange}>
                    <SelectTrigger id="aniva-appointment-select">
                        <SelectValue placeholder={isLoadingAppointments ? "Termine werden geladen..." : "Termin auswählen..."} />
                    </SelectTrigger>
                    <SelectContent>
                        {appointments?.map((appointment) => (
                            <SelectItem
                                key={appointment.id}
                                value={appointment.id}
                                disabled={appointment.assignedBloodReport !== null}
                            >
                                <div className="flex flex-col">
                                    <span>{appointment.displayName}</span>
                                    {appointment.dateFromLocal && (
                                        <span className="text-xs text-muted-foreground">
                                            {formatAppointmentDate(appointment.dateFromLocal)}
                                        </span>
                                    )}
                                    {appointment.assignedBloodReport && (
                                        <span className="text-xs text-destructive">Bereits zugewiesen</span>
                                    )}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-2">
                <Label>Panels auswählen</Label>
                <Select
                    value=""
                    onValueChange={addPanel}
                    disabled={!hasAppointment}
                >
                    <SelectTrigger className={!hasAppointment ? 'opacity-50' : ''}>
                        <SelectValue placeholder={!hasAppointment ? "Erst Termin auswählen..." : "Panel hinzufügen..."} />
                    </SelectTrigger>
                    <SelectContent>
                        {availablePanelsToAdd.map((panel) => (
                            <SelectItem key={panel.id} value={panel.anivaPanelId!}>
                                {panel.name}
                            </SelectItem>
                        ))}
                        {availablePanelsToAdd.length === 0 && (
                            <SelectItem value="__none__" disabled>
                                Alle Panels bereits ausgewählt
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
                {selectedPanelDetails.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        {selectedPanelDetails.map((panel) => (
                            <Badge
                                key={panel.id}
                                variant="secondary"
                                className="flex items-center gap-1 pr-1"
                            >
                                {panel.name}
                                <button
                                    type="button"
                                    className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                                    onClick={() => removePanel(panel.anivaPanelId!)}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </Badge>
                        ))}
                    </div>
                )}
                {hasAppointment && selectedPanelIds.size === 0 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Mindestens ein Panel muss ausgewählt werden.
                    </p>
                )}
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <Button
                onClick={handleCreate}
                disabled={isCreating || !hasAppointment || selectedPanelIds.size === 0}
            >
                {isCreating ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Erstellen...
                    </>
                ) : (
                    'Aniva Labor-Auftrag erstellen'
                )}
            </Button>
        </div>
    );
};

const anivaStatusMap: Record<string, { label: string; color: string; bgColor: string }> = {
    pending: { label: 'Ausstehend', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
    confirmed: { label: 'Bestätigt', color: 'text-blue-700', bgColor: 'bg-blue-100' },
    blood_drawn: { label: 'Blut abgenommen', color: 'text-green-700', bgColor: 'bg-green-100' },
    shipment_picked_up: { label: 'Abholung erfolgt', color: 'text-green-700', bgColor: 'bg-green-100' },
    cancelled: { label: 'Storniert', color: 'text-gray-700', bgColor: 'bg-gray-100' },
};

const AnivaConfirmBloodDrawDialog = ({
    isOpen,
    onClose,
    bloodReportId,
    anivaAppointmentId,
    attachedAnivaPanelIds,
    storeId,
    customerId,
}: {
    isOpen: boolean;
    onClose: () => void;
    bloodReportId: string;
    anivaAppointmentId: string;
    attachedAnivaPanelIds: string[];
    storeId: number | null;
    customerId: number;
}) => {
    const [selectedActivationIdId, setSelectedActivationIdId] = useState('');
    const [activationSearch, setActivationSearch] = useState('');
    const [isConfirming, setIsConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { data: panels } = trpc.bloodTests.listPanels.useQuery();
    const { data: availableActivationIds, isLoading: isLoadingActivationIds } =
        trpc.bloodTests.getAvailableActivationIdsForStore.useQuery(
            { storeId: storeId! },
            { enabled: isOpen && storeId !== null },
        );

    const anivaPanels = useMemo(
        () => (panels ?? []).filter((p) => !!p.anivaPanelId),
        [panels],
    );

    const attachedSet = useMemo(
        () => new Set(attachedAnivaPanelIds),
        [attachedAnivaPanelIds],
    );

    const attachedPanels = useMemo(
        () => anivaPanels.filter((p) => attachedSet.has(p.anivaPanelId!)),
        [anivaPanels, attachedSet],
    );

    const unattachedPanels = useMemo(
        () => anivaPanels.filter((p) => !attachedSet.has(p.anivaPanelId!)),
        [anivaPanels, attachedSet],
    );

    const activationIdItems = useMemo(
        () => (availableActivationIds ?? []).map((a) => ({
            value: a.id,
            label: a.activationCode,
        })),
        [availableActivationIds],
    );

    const filteredActivationIdItems = useMemo(() => {
        const q = activationSearch.trim().toLowerCase();
        if (!q) return [];
        return activationIdItems.filter((item) =>
            item.label.toLowerCase().includes(q),
        );
    }, [activationSearch, activationIdItems]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedActivationIdId('');
            setActivationSearch('');
            setError(null);
        }
    }, [isOpen]);

    const trpcUtils = trpc.useUtils();

    const addPanelMutation = trpc.customers.addAnivaPanelToAppointment.useMutation({
        onSuccess: () => {
            trpcUtils.customers.listAnivaLabOrders.invalidate({ customerId });
        },
        onError: (err) => {
            toast.error(err.message || 'Fehler beim Hinzufügen des Panels');
        },
    });

    const removePanelMutation = trpc.customers.removeAnivaPanelFromAppointment.useMutation({
        onSuccess: () => {
            trpcUtils.customers.listAnivaLabOrders.invalidate({ customerId });
        },
        onError: (err) => {
            toast.error(err.message || 'Fehler beim Entfernen des Panels');
        },
    });

    const confirmMutation = trpc.customers.confirmAnivaBloodDraw.useMutation({
        onSuccess: () => {
            toast.success('Blutentnahme erfolgreich bestätigt');
            trpcUtils.customers.listAnivaLabOrders.invalidate({ customerId });
            trpcUtils.bloodTests.getAvailableActivationIdsForStore.invalidate();
            onClose();
        },
        onError: (err) => {
            setError(err.message || 'Fehler beim Bestätigen der Blutentnahme');
        },
    });

    const handleConfirm = async () => {
        setError(null);
        setIsConfirming(true);

        try {
            await confirmMutation.mutateAsync({
                bloodReportId,
                activationIdId: selectedActivationIdId,
            });
        } finally {
            setIsConfirming(false);
        }
    };

    const isPanelMutating = addPanelMutation.isPending || removePanelMutation.isPending;
    const canConfirm = attachedAnivaPanelIds.length > 0 && selectedActivationIdId.length > 0;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Blutentnahme bestätigen</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        Aniva Termin: <span className="font-mono">{anivaAppointmentId}</span>
                    </p>

                    <div className="space-y-2">
                        <Label>Panels verwalten</Label>
                        <div className="border rounded-md p-3 space-y-1 max-h-64 overflow-y-auto">
                            {anivaPanels.length === 0 ? (
                                <span className="text-sm text-muted-foreground">Keine Aniva-Panels verfügbar.</span>
                            ) : (
                                <>
                                    {attachedPanels.map((panel) => (
                                        <div
                                            key={panel.id}
                                            className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-green-50 border border-green-200 dark:bg-green-950/30 dark:border-green-800"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                                                <span className="text-sm font-medium">{panel.name}</span>
                                                <span className="text-xs text-muted-foreground font-mono truncate">
                                                    ({panel.anivaPanelId})
                                                </span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                disabled={isPanelMutating}
                                                onClick={() => removePanelMutation.mutate({
                                                    bloodReportId,
                                                    anivaPanelId: panel.anivaPanelId!,
                                                })}
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    ))}
                                    {attachedPanels.length > 0 && unattachedPanels.length > 0 && (
                                        <Separator className="my-1" />
                                    )}
                                    {unattachedPanels.map((panel) => (
                                        <div
                                            key={panel.id}
                                            className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-accent"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                                                <span className="text-sm">{panel.name}</span>
                                                <span className="text-xs text-muted-foreground font-mono truncate">
                                                    ({panel.anivaPanelId})
                                                </span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 shrink-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                                disabled={isPanelMutating}
                                                onClick={() => addPanelMutation.mutate({
                                                    bloodReportId,
                                                    anivaPanelId: panel.anivaPanelId!,
                                                })}
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    ))}
                                </>
                            )}
                            {isPanelMutating && (
                                <div className="flex items-center justify-center py-1">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                            )}
                        </div>
                        {attachedAnivaPanelIds.length === 0 && (
                            <p className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Mindestens ein Panel muss zugewiesen sein.
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label>Barcode (Aktivierungs-ID)</Label>
                        {storeId === null ? (
                            <div className="border rounded-md p-3 text-sm text-muted-foreground">
                                Kein Store verknüpft — Barcode-Auswahl nicht möglich.
                            </div>
                        ) : (
                            <AutoComplete
                                selectedValue={selectedActivationIdId}
                                onSelectedValueChange={setSelectedActivationIdId}
                                searchValue={activationSearch}
                                onSearchValueChange={setActivationSearch}
                                items={filteredActivationIdItems}
                                isLoading={isLoadingActivationIds}
                                placeholder="Barcode suchen..."
                                emptyMessage={activationSearch.trim() ? "Kein passender Barcode gefunden." : "Barcode eingeben, um zu suchen."}
                            />
                        )}
                    </div>

                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                        <Button onClick={handleConfirm} disabled={!canConfirm || isConfirming}>
                            {isConfirming ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : null}
                            Bestätigen
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

type PanelDetailRecord = {
    name: string;
    anivaPanelId: string | null;
    description: string | null;
    edtaTubeCount: number;
    serumTubeCount: number;
    fluoridTubeCount: number;
    handlingInstructions: unknown;
};

const PanelDetailBlock = ({ panel }: { panel: PanelDetailRecord }) => (
    <div className="bg-muted/50 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
            <div>
                <span className="font-medium">{panel.name}</span>
                {panel.anivaPanelId && (
                    <span className="text-xs text-muted-foreground ml-2 font-mono">
                        ({panel.anivaPanelId})
                    </span>
                )}
            </div>
        </div>

        {panel.description && (
            <p className="text-xs text-muted-foreground">
                {panel.description}
            </p>
        )}

        <div className="flex flex-wrap gap-3 text-sm pb-3 border-b">
            {panel.edtaTubeCount > 0 && (
                <div className="flex items-center gap-1.5 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="font-medium">{panel.edtaTubeCount}x EDTA (rot)</span>
                </div>
            )}
            {panel.serumTubeCount > 0 && (
                <div className="flex items-center gap-1.5 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded">
                    <span className="w-3 h-3 rounded-full bg-amber-800"></span>
                    <span className="font-medium">{panel.serumTubeCount}x Serum (braun)</span>
                </div>
            )}
            {panel.fluoridTubeCount > 0 && (
                <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-900/30 px-2 py-1 rounded">
                    <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                    <span className="font-medium">{panel.fluoridTubeCount}x GlucoEXACT (grau)</span>
                </div>
            )}
        </div>

        {!!panel.handlingInstructions && (
            <div className="space-y-3">
                <h5 className="text-sm font-semibold">Handhabungsanweisungen</h5>
                {panel.edtaTubeCount > 0 && (panel.handlingInstructions as { edta?: { centrifuge: boolean; storage: string; notes: string } })?.edta && (
                    <div className="border-l-4 border-red-500 pl-3 py-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">EDTA (rot)</span>
                            <Badge variant="outline" className="text-xs">
                                {(panel.handlingInstructions as { edta?: { centrifuge: boolean } })?.edta?.centrifuge ? 'Zentrifugieren' : 'Nicht zentrifugieren'}
                            </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            <span className="font-medium">Lagerung:</span> {(panel.handlingInstructions as { edta?: { storage: string } })?.edta?.storage}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {(panel.handlingInstructions as { edta?: { notes: string } })?.edta?.notes}
                        </p>
                    </div>
                )}
                {panel.serumTubeCount > 0 && (panel.handlingInstructions as { serum?: { centrifuge: boolean; centrifugeSettings: string; storage: string; notes: string } })?.serum && (
                    <div className="border-l-4 border-amber-800 pl-3 py-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">Serum (braun)</span>
                            <Badge variant="outline" className="text-xs">
                                {(panel.handlingInstructions as { serum?: { centrifuge: boolean } })?.serum?.centrifuge ? 'Zentrifugieren' : 'Nicht zentrifugieren'}
                            </Badge>
                        </div>
                        {(panel.handlingInstructions as { serum?: { centrifugeSettings: string } })?.serum?.centrifugeSettings && (
                            <p className="text-xs text-muted-foreground">
                                <span className="font-medium">Zentrifuge:</span> {(panel.handlingInstructions as { serum?: { centrifugeSettings: string } })?.serum?.centrifugeSettings}
                            </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                            <span className="font-medium">Lagerung:</span> {(panel.handlingInstructions as { serum?: { storage: string } })?.serum?.storage}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {(panel.handlingInstructions as { serum?: { notes: string } })?.serum?.notes}
                        </p>
                    </div>
                )}
                {panel.fluoridTubeCount > 0 && (panel.handlingInstructions as { fluorid?: { centrifuge: boolean; centrifugeSettings: string; storage: string; notes: string } })?.fluorid && (
                    <div className="border-l-4 border-gray-500 pl-3 py-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">GlucoEXACT (grau)</span>
                            <Badge variant="outline" className="text-xs">
                                {(panel.handlingInstructions as { fluorid?: { centrifuge: boolean } })?.fluorid?.centrifuge ? 'Zentrifugieren' : 'Nicht zentrifugieren'}
                            </Badge>
                        </div>
                        {(panel.handlingInstructions as { fluorid?: { centrifugeSettings: string } })?.fluorid?.centrifugeSettings && (
                            <p className="text-xs text-muted-foreground">
                                <span className="font-medium">Zentrifuge:</span> {(panel.handlingInstructions as { fluorid?: { centrifugeSettings: string } })?.fluorid?.centrifugeSettings}
                            </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                            <span className="font-medium">Lagerung:</span> {(panel.handlingInstructions as { fluorid?: { storage: string } })?.fluorid?.storage}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {(panel.handlingInstructions as { fluorid?: { notes: string } })?.fluorid?.notes}
                        </p>
                    </div>
                )}
            </div>
        )}
    </div>
);

const AnivaLabOrdersList = ({ customerId }: { customerId: number }) => {
    const { data: labOrders, isLoading, isFetching, isError, refetch } = trpc.customers.listAnivaLabOrders.useQuery(
        { customerId },
        { refetchInterval: 30000, refetchIntervalInBackground: false },
    );
    const { data: allPanels } = trpc.bloodTests.listPanels.useQuery();

    const panelsByAnivaPanelId = useMemo(() => {
        const map = new Map<string, PanelDetailRecord>();
        for (const p of allPanels ?? []) {
            if (p.anivaPanelId) {
                map.set(p.anivaPanelId, p);
            }
        }
        return map;
    }, [allPanels]);

    const trpcUtils = trpc.useUtils();

    const cancelAnivaLabOrderMutation = trpc.customers.cancelAnivaLabOrder.useMutation({
        onSuccess: () => {
            toast.success('Aniva-Termin erfolgreich storniert (Aktivierungs-ID wurde freigegeben)');
            trpcUtils.customers.listAnivaLabOrders.invalidate({ customerId });
            trpcUtils.bloodTests.listActivationIds.invalidate();
        },
        onError: (err) => {
            toast.error(err.message || 'Fehler beim Stornieren des Aniva-Termins');
        },
    });

    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDialogOrder, setConfirmDialogOrder] = useState<{
        bloodReportId: string;
        anivaAppointmentId: string;
        storeId: number | null;
    } | null>(null);

    const toggleExpanded = (id: string) => {
        setExpandedItems((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const formatAppointmentDate = (dateLocalSerialized: string | null) => {
        if (!dateLocalSerialized) return '';
        try { return LocalDateTime.deserialize(dateLocalSerialized).getDisplayDateTime(); }
        catch { return ''; }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
            </div>
        );
    }

    if (isError) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Fehler</AlertTitle>
                <AlertDescription>
                    Die Aniva Labor-Aufträge konnten nicht geladen werden.
                </AlertDescription>
                <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
                    <RefreshCw className="mr-2 h-4 w-4" /> Erneut versuchen
                </Button>
            </Alert>
        );
    }

    if (!labOrders || labOrders.length === 0) {
        return (
            <div className="text-muted-foreground text-sm py-4">
                Keine Aniva Labor-Aufträge vorhanden.
            </div>
        );
    }

    return (
        <>
            <AnivaConfirmBloodDrawDialog
                isOpen={confirmDialogOpen}
                onClose={() => { setConfirmDialogOpen(false); setConfirmDialogOrder(null); }}
                bloodReportId={confirmDialogOrder?.bloodReportId ?? ''}
                anivaAppointmentId={confirmDialogOrder?.anivaAppointmentId ?? ''}
                attachedAnivaPanelIds={
                    labOrders?.find(o => o.id === confirmDialogOrder?.bloodReportId)
                        ?.anivaData?.panels?.map(p => p.panel_id) ?? []
                }
                storeId={confirmDialogOrder?.storeId ?? null}
                customerId={customerId}
            />

            <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                title="Aktualisieren"
                disabled={isFetching}
            >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>

            <div className="w-full space-y-4">
                {labOrders.map((order) => {
                    const anivaStatus = (order.anivaData?.status ?? order.anivaAppointmentStatus) || 'unknown';
                    const statusInfo = anivaStatusMap[anivaStatus];
                    const isExpanded = expandedItems.has(order.id);
                    const canConfirmDraw = ['pending', 'confirmed'].includes(anivaStatus);

                    return (
                        <div key={order.id} className="border rounded-lg p-4 space-y-3">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => toggleExpanded(order.id)}
                                    >
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </Button>
                                    <span className="font-mono text-sm font-medium">
                                        {order.anivaAppointmentId?.slice(0, 8)}...
                                    </span>
                                    {order.anivaData?.scheduled_at && (
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(order.anivaData.scheduled_at).toLocaleDateString('de-DE', {
                                                year: 'numeric', month: '2-digit', day: '2-digit',
                                                hour: '2-digit', minute: '2-digit',
                                            })}
                                        </span>
                                    )}
                                    {statusInfo && (
                                        <Badge className={`${statusInfo.bgColor} ${statusInfo.color} border-0`}>
                                            {statusInfo.label}
                                        </Badge>
                                    )}
                                    {order.anivaBarcode && (
                                        <span className="text-xs font-mono text-muted-foreground">
                                            Barcode: {order.anivaBarcode}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {canConfirmDraw && (
                                        <>
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    setConfirmDialogOrder({
                                                        bloodReportId: order.id,
                                                        anivaAppointmentId: order.anivaAppointmentId!,
                                                        storeId: order.appointment?.store?.id ?? null,
                                                    });
                                                    setConfirmDialogOpen(true);
                                                }}
                                            >
                                                Panel / Barcode hinzufügen
                                            </Button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        variant="destructive"
                                                        size="sm"
                                                        disabled={cancelAnivaLabOrderMutation.isPending}
                                                    >
                                                        {cancelAnivaLabOrderMutation.isPending ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <X className="h-4 w-4 mr-1" />
                                                                Stornieren
                                                            </>
                                                        )}
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Aniva-Termin stornieren?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Der Aniva-Termin wird unwiderruflich storniert. Falls eine Aktivierungs-ID zugewiesen ist, wird diese freigegeben.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => cancelAnivaLabOrderMutation.mutate({ bloodReportId: order.id })}
                                                            className="bg-destructive text-white hover:bg-destructive/90"
                                                        >
                                                            Stornieren
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => toggleExpanded(order.id)}
                                    >
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            {/* Expanded */}
                            {isExpanded && (
                                <>
                                    {/* Linked Appointment */}
                                    {order.appointment && (
                                        <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-md p-2">
                                            <Link2 className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-muted-foreground">Verknüpfter Termin:</span>
                                            <span className="font-medium">{order.appointment.displayName}</span>
                                            {order.appointment.dateFromLocal && (
                                                <span className="text-muted-foreground">
                                                    ({formatAppointmentDate(order.appointment.dateFromLocal)})
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Panel & Tube Info with Handling Instructions */}
                                    {(() => {
                                        const apiPanels = order.anivaData?.panels;
                                        if (apiPanels && apiPanels.length > 0) {
                                            return apiPanels.map((ap) => {
                                                const resolved = panelsByAnivaPanelId.get(ap.panel_id);
                                                if (resolved) {
                                                    return <PanelDetailBlock key={ap.panel_id} panel={resolved} />;
                                                }
                                                return (
                                                    <div key={ap.panel_id} className="bg-muted/50 rounded-lg p-4">
                                                        <span className="text-sm font-mono">{ap.panel_id}</span>
                                                        <span className="text-xs text-muted-foreground ml-2">(Unbekanntes Panel)</span>
                                                    </div>
                                                );
                                            });
                                        }
                                        return (
                                            <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                                                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                                                <span className="text-sm text-amber-700 dark:text-amber-400">Kein Panel zugewiesen</span>
                                            </div>
                                        );
                                    })()}

                                    {/* Aniva details */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        {order.anivaData && (
                                            <>
                                                <div>
                                                    <span className="text-muted-foreground block">Aniva ID</span>
                                                    <span className="font-mono text-xs break-all">{order.anivaData.id}</span>
                                                </div>
                                                <div>
                                                    <span className="text-muted-foreground block">Profil-ID</span>
                                                    <span className="font-mono text-xs break-all">{order.anivaData.profile_id}</span>
                                                </div>
                                                <div>
                                                    <span className="text-muted-foreground block">Status</span>
                                                    <span>{anivaStatusMap[order.anivaData.status]?.label ?? order.anivaData.status}</span>
                                                </div>
                                                {order.anivaData.scheduled_at && (
                                                    <div>
                                                        <span className="text-muted-foreground block">Geplant</span>
                                                        <span>
                                                            {new Date(order.anivaData.scheduled_at).toLocaleDateString('de-DE', {
                                                                year: 'numeric', month: '2-digit', day: '2-digit',
                                                                hour: '2-digit', minute: '2-digit',
                                                            })}
                                                        </span>
                                                    </div>
                                                )}
                                                {order.anivaData.created_at && (
                                                    <div>
                                                        <span className="text-muted-foreground block">Erstellt am</span>
                                                        <span>
                                                            {new Date(order.anivaData.created_at).toLocaleDateString('de-DE', {
                                                                year: 'numeric', month: '2-digit', day: '2-digit',
                                                                hour: '2-digit', minute: '2-digit',
                                                            })}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {order.anivaBarcode && (
                                            <div>
                                                <span className="text-muted-foreground block">Barcode</span>
                                                <span className="font-mono">{order.anivaBarcode}</span>
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-muted-foreground block">Erstellt (DB)</span>
                                            <span>
                                                {new Date(order.createdAt).toLocaleDateString('de-DE', {
                                                    year: 'numeric', month: '2-digit', day: '2-digit',
                                                    hour: '2-digit', minute: '2-digit',
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

type CustomerDocumentUploaderProps = {
    customerId: number;
}

// Informed consent templates for the Aufklärung creation
const INFORMED_CONSENT_TEMPLATES: Record<string, { id: string; name: string }> = {
    "ec930b72-769f-44ed-9995-4509dde2c65d": { id: "ec930b72-769f-44ed-9995-4509dde2c65d", name: "Botulinumtoxin" },
    "7aba93d6-4c7c-4678-825d-b0ee53173efb": { id: "7aba93d6-4c7c-4678-825d-b0ee53173efb", name: "PRP-Eigenbluttherapie" },
    "3ce56d6a-a655-4025-8dc2-4c803dee56f4": { id: "3ce56d6a-a655-4025-8dc2-4c803dee56f4", name: "Gewebeunterspritzung mit Fillern oder Biostimulatoren" },
    "b5b3dd9c-6f60-4e71-b214-8ffcd233e6f9": { id: "b5b3dd9c-6f60-4e71-b214-8ffcd233e6f9", name: "Lasertherapie_IPL" },
    "6a4d943c-ab62-4125-8c2b-25a118889b38": { id: "6a4d943c-ab62-4125-8c2b-25a118889b38", name: "Hautpeeling - Microneedling - Microdermabrasion (inkl. Retinol-Peeling 3 %)" },
    "bf522c81-90f7-4333-a638-d9807587b90c": { id: "bf522c81-90f7-4333-a638-d9807587b90c", name: "Ergänzende Patientenaufklärung für Wiederholungs- und Folgetermin" },
    "185ddf89-74b0-45e4-b90f-399de99c3e5b": { id: "185ddf89-74b0-45e4-b90f-399de99c3e5b", name: "Lipolyse (Fettweg-Spritze)" },
    "db0f35cb-9504-4244-8498-aedd4768c59e": { id: "db0f35cb-9504-4244-8498-aedd4768c59e", name: "Aufklärung zur dermatologischen Sprechstunde und topischen Therapien bei Akne, Rosazea und seborrhoischem Ekzem" },
    "5fa88e85-c65c-486d-bc76-873a6d4ac2be": { id: "5fa88e85-c65c-486d-bc76-873a6d4ac2be", name: "Aufklärung Radiofrequenz-Microneedling (Exion / Genius / Morpheus8)" },
    "c1850e9b-8b4f-437a-90c2-1b48c31382ce": { id: "c1850e9b-8b4f-437a-90c2-1b48c31382ce", name: "Infusionen und Labordiagnostik" },
    "7686d5a1-d99f-4ffa-a55d-123ac38d9e35": { id: "7686d5a1-d99f-4ffa-a55d-123ac38d9e35", name: "Isotretinoin Checklist" },
    "9609f789-5513-4a27-a3e6-ad6b5ad80c13": { id: "9609f789-5513-4a27-a3e6-ad6b5ad80c13", name: "Isotretinoin Aufklärung" },
    "fd667a0e-19b5-4551-a593-2ad4bb5131b1": { id: "fd667a0e-19b5-4551-a593-2ad4bb5131b1", name: "Operative Lidstraffung" },
    "920528a8-3cec-47c2-8d69-0991989cc0bf": { id: "920528a8-3cec-47c2-8d69-0991989cc0bf", name: "Operative Narbenkorrektur" },
    "f8bee0d0-8dea-4321-914a-b81610fc9e7e": { id: "f8bee0d0-8dea-4321-914a-b81610fc9e7e", name: "EMSCULPT" },
    "aac67874-7855-4d65-bd98-f40ba0e8427a": { id: "aac67874-7855-4d65-bd98-f40ba0e8427a", name: "Aufklärung über die Anwendung von Hyaluronidase" },
    "b9ad28e6-0599-44b2-a19f-78fc130bb3a5": { id: "b9ad28e6-0599-44b2-a19f-78fc130bb3a5", name: "Aufklärung zur Lachgasanalgesie (Livopan®) in der ästhetischen Medizin" },
    "79a718ab-c311-48c6-878d-88b72cd40003": { id: "79a718ab-c311-48c6-878d-88b72cd40003", name: "Xanthelasmen" },
    "48b33f23-3fa1-414f-bd3f-7f124e58b483": { id: "48b33f23-3fa1-414f-bd3f-7f124e58b483", name: "Mama Facial" },
    "9f871e8c-9926-4331-80b6-32e78098896a": { id: "9f871e8c-9926-4331-80b6-32e78098896a", name: "Aufklärung zur Narbentherapie" },
};

const TEMPLATES_ALLOWED_FOR_COSMETICIANS = [
    "6a4d943c-ab62-4125-8c2b-25a118889b38",
    "5fa88e85-c65c-486d-bc76-873a6d4ac2be",
    "b5b3dd9c-6f60-4e71-b214-8ffcd233e6f9",
    "48b33f23-3fa1-414f-bd3f-7f124e58b483",
];

const CustomerDocumentUploader = ({ customerId }: CustomerDocumentUploaderProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState<CustomerDocumentType | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Informed consent specific state
    const [selectedStore, setSelectedStore] = useState<{ id: number; name: string } | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<{ id: string; name: string } | null>(null);
    const [selectedPractitioner, setSelectedPractitioner] = useState<{ id: number; firstName: string; lastName: string } | null>(null);

    const trpcUtils = trpc.useUtils();
    const markDocumentUploadCompletedMutation = trpc.customers.markDocumentUploadCompleted.useMutation();
    const generateInformedConsentUrlMutation = trpc.customers.generateInformedConsentUrl.useMutation();

    // Fetch stores and practitioners for informed consent
    const allStoresQuery = trpc.stores.getAll.useQuery({ pageSize: 1000 });
    const allStores = useMemo(() => allStoresQuery.data?.data ?? [], [allStoresQuery.data]);

    const allPractitionersQuery = trpc.practitioners.getAll.useQuery(
        { pageSize: 1000, filters: { storeId: selectedStore?.id?.toString() } },
        { enabled: !!selectedStore }
    );

    const allPractitioners = useMemo(() => {
        if (!allPractitionersQuery.data?.data) return [];
        const doctorCategoryId = 1;
        const cosmeticianCategoryId = 2;

        if (selectedTemplate?.id && TEMPLATES_ALLOWED_FOR_COSMETICIANS.includes(selectedTemplate.id)) {
            return allPractitionersQuery.data.data.filter(
                (p) => p.categoryId === doctorCategoryId || p.categoryId === cosmeticianCategoryId
            );
        }
        return allPractitionersQuery.data.data.filter((p) => p.categoryId === doctorCategoryId);
    }, [allPractitionersQuery.data, selectedTemplate]);

    const isInformedConsentMode = selectedDocumentType === CustomerDocumentType.INFORMED_CONSENT;

    const resetForm = useCallback(() => {
        setSelectedFile(null);
        setSelectedDocumentType(null);
        setUploadProgress(0);
        setUploadStatus('idle');
        setErrorMessage('');
        setSelectedStore(null);
        setSelectedTemplate(null);
        setSelectedPractitioner(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleDialogOpenChange = (open: boolean) => {
        setIsDialogOpen(open);
        if (!open) {
            resetForm();
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setUploadStatus('idle');
            setErrorMessage('');
            setUploadProgress(0);
        }
    };

    const handleDocumentTypeChange = (value: string) => {
        setSelectedDocumentType(value as CustomerDocumentType);
        setUploadStatus('idle');
        setErrorMessage('');
        // Reset informed consent fields when switching types
        setSelectedStore(null);
        setSelectedTemplate(null);
        setSelectedPractitioner(null);
        setSelectedFile(null);
    };

    const handleUpload = async () => {
        if (!selectedFile || !selectedDocumentType) return;

        setIsUploading(true);
        setUploadStatus('uploading');
        setUploadProgress(0);

        try {
            const signedUploadData = await trpcUtils.customers.getSignedDocumentUploadUrl.fetch({
                customerId,
                fileName: selectedFile.name,
                contentType: selectedFile.type,
                documentType: selectedDocumentType,
            });
            if (!signedUploadData) {
                setErrorMessage('Failed to generate signed s3 upload URL.');
                return;
            }

            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = (event.loaded / event.total) * 100;
                    setUploadProgress(percentComplete);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    markDocumentUploadCompletedMutation.mutateAsync({
                        documentId: signedUploadData.documentId,
                    });
                    setUploadStatus('success');
                    setUploadProgress(100);
                    trpcUtils.customers.listDocuments.refetch();
                    setTimeout(() => {
                        setIsDialogOpen(false);
                        resetForm();
                    }, 1500);
                } else {
                    setUploadStatus('error');
                    setErrorMessage(`Upload failed with status: ${xhr.status}`);
                }
                setIsUploading(false);
            });

            xhr.addEventListener('error', () => {
                setUploadStatus('error');
                setErrorMessage('Upload failed due to network error');
                setIsUploading(false);
            });

            xhr.open('PUT', signedUploadData.uploadUrl);
            xhr.setRequestHeader('Content-Type', selectedFile.type);
            xhr.send(selectedFile);

        } catch (error) {
            setUploadStatus('error');
            setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
            setIsUploading(false);
        }
    };

    const handleCreateInformedConsent = async () => {
        if (!selectedStore || !selectedTemplate || !selectedPractitioner) return;

        setIsUploading(true);
        setUploadStatus('uploading');

        try {
            const link = await generateInformedConsentUrlMutation.mutateAsync({
                customerId,
                practitionerId: selectedPractitioner.id,
                storeId: selectedStore.id,
                templateId: selectedTemplate.id,
            });

            setUploadStatus('success');
            window.open(link, '_blank');

            setTimeout(() => {
                setIsDialogOpen(false);
                resetForm();
                trpcUtils.customers.listInformedConsents.refetch();
            }, 1000);
        } catch (error) {
            setUploadStatus('error');
            setErrorMessage(error instanceof Error ? error.message : 'Fehler beim Erstellen der Aufklärung');
        } finally {
            setIsUploading(false);
        }
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const canUpload = selectedFile && selectedDocumentType && !isUploading && !isInformedConsentMode;
    const canCreateInformedConsent = isInformedConsentMode && selectedStore && selectedTemplate && selectedPractitioner && !isUploading;

    const handleDropdownSelect = (type: CustomerDocumentType) => {
        setSelectedDocumentType(type);
        setIsDialogOpen(true);
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Dokument hinzufügen
                        <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {Object.values(CustomerDocumentType).map((type) => (
                        <DropdownMenuItem key={type} onClick={() => handleDropdownSelect(type)}>
                            {getCustomerDocumentTypeLabel(type)}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogContent className="sm:max-w-md max-w-[calc(100vw-2rem)] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {isInformedConsentMode ? (
                                <>
                                    <FileText className="h-5 w-5" />
                                    Aufklärung erstellen
                                </>
                            ) : (
                                <>
                                    <Upload className="h-5 w-5" />
                                    Dokument hochladen ({selectedDocumentType ? getCustomerDocumentTypeLabel(selectedDocumentType) : ''})
                                </>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 overflow-hidden">

                        {/* Informed Consent Creation UI */}
                        {isInformedConsentMode && (
                            <>
                                <div className="space-y-2">
                                    <Label>Standort</Label>
                                    <Select
                                        value={selectedStore?.id?.toString() || ''}
                                        onValueChange={(value) => {
                                            const store = allStores.find((s) => s.id.toString() === value);
                                            setSelectedStore(store ? { id: store.id, name: store.name } : null);
                                            setSelectedPractitioner(null);
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Standort auswählen" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {allStores.map((store) => (
                                                <SelectItem key={store.id} value={store.id.toString()}>
                                                    {store.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2 min-w-0">
                                    <Label>Aufklärung Vorlage</Label>
                                    <Select
                                        value={selectedTemplate?.id || ''}
                                        onValueChange={(value) => {
                                            const template = INFORMED_CONSENT_TEMPLATES[value];
                                            setSelectedTemplate(template || null);
                                            setSelectedPractitioner(null);
                                        }}
                                    >
                                        <SelectTrigger className="w-full overflow-hidden">
                                            <span className="truncate block">
                                                {selectedTemplate?.name || "Vorlage auswählen"}
                                            </span>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.values(INFORMED_CONSENT_TEMPLATES).map((template) => (
                                                <SelectItem key={template.id} value={template.id}>
                                                    {template.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Behandler</Label>
                                    <Select
                                        value={selectedPractitioner?.id?.toString() || ''}
                                        onValueChange={(value) => {
                                            const practitioner = allPractitioners.find((p) => p.id.toString() === value);
                                            setSelectedPractitioner(practitioner ? { id: practitioner.id, firstName: practitioner.firstName, lastName: practitioner.lastName } : null);
                                        }}
                                        disabled={!selectedStore || !selectedTemplate}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={!selectedStore ? "Erst Standort auswählen" : !selectedTemplate ? "Erst Vorlage auswählen" : "Behandler auswählen"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {allPractitioners.map((practitioner) => (
                                                <SelectItem key={practitioner.id} value={practitioner.id.toString()}>
                                                    <PractitionerNameWithCategory
                                                        firstName={practitioner.firstName}
                                                        lastName={practitioner.lastName}
                                                        title={practitioner.title}
                                                        categoryName={practitioner.practitioner_category?.name}
                                                    />
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}

                        {/* File Upload UI */}
                        {!isInformedConsentMode && selectedDocumentType && (
                            <>
                                <div className="space-y-2">
                                    <Label>Datei</Label>
                                    <Input
                                        ref={fileInputRef}
                                        type="file"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.json"
                                    />

                                    <Button
                                        onClick={handleButtonClick}
                                        variant="outline"
                                        className="w-full"
                                        disabled={isUploading}
                                    >
                                        <File className="h-4 w-4 mr-2" />
                                        {selectedFile ? 'Andere Datei auswählen' : 'Datei auswählen'}
                                    </Button>
                                </div>

                                {selectedFile && (
                                    <div className="flex items-center justify-between text-sm text-muted-foreground p-2 bg-muted rounded-md">
                                        <span className="truncate flex-1 mr-2">{selectedFile.name}</span>
                                        <span className="shrink-0">{formatFileSize(selectedFile.size)}</span>
                                    </div>
                                )}
                            </>
                        )}

                        {uploadStatus === 'uploading' && !isInformedConsentMode && (
                            <div className="space-y-2">
                                <Progress value={uploadProgress} className="w-full" />
                                <p className="text-sm text-muted-foreground text-center">
                                    {Math.round(uploadProgress)}% hochgeladen
                                </p>
                            </div>
                        )}

                        {uploadStatus === 'success' && (
                            <Alert className="border-green-200 bg-green-50">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <AlertDescription className="text-green-800">
                                    {isInformedConsentMode ? 'Aufklärung wurde geöffnet!' : 'Dokument erfolgreich hochgeladen!'}
                                </AlertDescription>
                            </Alert>
                        )}

                        {uploadStatus === 'error' && (
                            <Alert className="border-red-200 bg-red-50">
                                <AlertCircle className="h-4 w-4 text-red-600" />
                                <AlertDescription className="text-red-800">
                                    {errorMessage}
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Submit Button */}
                        {isInformedConsentMode ? (
                            <Button
                                onClick={handleCreateInformedConsent}
                                disabled={!canCreateInformedConsent}
                                className="w-full"
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Wird erstellt...
                                    </>
                                ) : (
                                    <>
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        Aufklärung starten
                                    </>
                                )}
                            </Button>
                        ) : selectedDocumentType && (
                            <Button
                                onClick={handleUpload}
                                disabled={!canUpload}
                                className="w-full"
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Wird hochgeladen...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Hochladen
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
type CustomerDocumentsListProps = {
    customerId: number;
};

function CustomerDocumentsList({ customerId }: CustomerDocumentsListProps) {
    const trpcUtils = trpc.useUtils();

    const {
        data: customerDocuments,
        isLoading,
        isError,
        refetch,
    } = trpc.customers.listDocuments.useQuery({ customerId });

    const archiveDocumentMutation = trpc.customers.archiveDocument.useMutation();

    const handleDownload = useCallback(async (documentId: string) => {
        try {
            const signedDownloadUrl = await trpcUtils.customers.getDocumentReadUrl.fetch({ documentId });
            if (!signedDownloadUrl?.downloadUrl) {
                toast.error('Fehler beim Abrufen des Download-Links.');
                return;
            }
            // Open the download in a new window/tab
            window.open(signedDownloadUrl.downloadUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            toast.error('Fehler beim Herunterladen des Dokuments.');
        }
    }, [trpcUtils]);

    const handleDelete = useCallback(async (documentId: string) => {
        try {
            await archiveDocumentMutation.mutateAsync({ documentId });
            toast.success('Dokument erfolgreich gelöscht.');
            refetch();
        } catch (error) {
            toast.error('Fehler beim Löschen des Dokuments.');
        }
    }, [archiveDocumentMutation, refetch]);

    if (isLoading) {
        return (
            <div className="flex flex-col gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
            </div>
        );
    }

    if (isError) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Fehler</AlertTitle>
                <AlertDescription>
                    Die Dokumente konnten nicht geladen werden.
                </AlertDescription>
                <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
                    <RefreshCw className="mr-2 h-4 w-4" /> Erneut versuchen
                </Button>
            </Alert>
        );
    }

    if (!customerDocuments || customerDocuments.length === 0) {
        return (
            <div className="text-muted-foreground text-sm py-4">
                Keine Dokumente vorhanden.
            </div>
        );
    }

    return (
        <div className="w-full">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead>Hochgeladen am</TableHead>
                        <TableHead>Aktionen</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {customerDocuments.map((doc) => (
                        <TableRow key={doc.id}>
                            <TableCell className="font-medium">{doc.name}</TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {getCustomerDocumentTypeLabel(doc.type)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {doc.createdAt
                                    ? format(new Date(doc.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })
                                    : '-'}
                            </TableCell>
                            <TableCell>
                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDownload(doc.id)}
                                        title="Herunterladen"
                                    >
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                title="Löschen"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Dokument löschen</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    Sind Sie sicher, dass Sie das Dokument "{doc.name}" löschen möchten?
                                                    Diese Aktion kann nicht rückgängig gemacht werden.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => handleDelete(doc.id)}
                                                    className="bg-destructive hover:bg-destructive/90"
                                                >
                                                    Löschen
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

// --- Unified Patient Documents Table ---
type UnifiedDocumentType = 'ANAMNESE' | 'AERZTLICHE_AUFKLAERUNG' | 'BLUTBEFUND' | 'BLUTREPORT' | 'SONSTIGES';

type UnifiedDocumentItem = {
    id: string;
    name: string;
    type: UnifiedDocumentType;
    createdAt: Date;
    // For actions
    documentId?: string;
    questionnaireResponseId?: number;
    bloodReportId?: string;
    status?: string;
    openHealthUrl?: string | null;
};

const getUnifiedDocumentTypeLabel = (type: UnifiedDocumentType): string => {
    const labels: Record<UnifiedDocumentType, string> = {
        'ANAMNESE': 'Anamnese',
        'AERZTLICHE_AUFKLAERUNG': 'Ärztliche Aufklärung',
        'BLUTBEFUND': 'Blutbefund',
        'BLUTREPORT': 'Blutreport',
        'SONSTIGES': 'Sonstiges',
    };
    return labels[type] || type;
};

type UnifiedPatientDocumentsTableProps = {
    customerId: number;
    actionButton?: React.ReactNode;
};

type SortColumn = 'name' | 'type' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const ALL_DOCUMENT_TYPES: UnifiedDocumentType[] = ['ANAMNESE', 'AERZTLICHE_AUFKLAERUNG', 'BLUTBEFUND', 'BLUTREPORT', 'SONSTIGES'];

function UnifiedPatientDocumentsTable({ customerId, actionButton }: UnifiedPatientDocumentsTableProps) {
    const trpcUtils = trpc.useUtils();

    // Filter state
    const [typeFilter, setTypeFilter] = useState<UnifiedDocumentType | 'ALL'>('ALL');
    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [isSelectingToDate, setIsSelectingToDate] = useState(false);

    // Sort state
    const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    // Blood report flow modal state
    const [flowModalReportId, setFlowModalReportId] = useState<string | null>(null);

    // Fetch all 4 data sources
    const {
        data: anamneseResponses,
        isLoading: isLoadingAnamnese,
    } = trpc.customers.listAnamneseQuestionnaireResponses.useQuery({ customerId });

    const {
        data: informedConsents,
        isLoading: isLoadingConsents,
    } = trpc.customers.listInformedConsents.useQuery({ customerId });

    const {
        data: documents,
        isLoading: isLoadingDocuments,
        refetch: refetchDocuments,
    } = trpc.customers.listDocuments.useQuery({ customerId });

    const {
        data: bloodReports,
        isLoading: isLoadingBloodReports,
    } = trpc.customers.listBloodReports.useQuery({ customerId });

    const archiveDocumentMutation = trpc.customers.archiveDocument.useMutation();

    const isLoading = isLoadingAnamnese || isLoadingConsents || isLoadingDocuments || isLoadingBloodReports;

    const handleDownload = useCallback(async (documentId: string) => {
        try {
            const signedDownloadUrl = await trpcUtils.customers.getDocumentReadUrl.fetch({ documentId });
            if (!signedDownloadUrl?.downloadUrl) {
                toast.error('Fehler beim Abrufen des Download-Links.');
                return;
            }
            window.open(signedDownloadUrl.downloadUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            toast.error('Fehler beim Herunterladen des Dokuments.');
        }
    }, [trpcUtils]);

    const handleDelete = useCallback(async (documentId: string) => {
        try {
            await archiveDocumentMutation.mutateAsync({ documentId });
            toast.success('Dokument erfolgreich gelöscht.');
            refetchDocuments();
        } catch (error) {
            toast.error('Fehler beim Löschen des Dokuments.');
        }
    }, [archiveDocumentMutation, refetchDocuments]);

    const handleSort = useCallback((column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection(column === 'createdAt' ? 'desc' : 'asc');
        }
    }, [sortColumn]);

    const getSortIcon = useCallback((column: SortColumn) => {
        if (sortColumn !== column) {
            return <ArrowUpDown className="ml-2 h-4 w-4" />;
        }
        return sortDirection === 'asc'
            ? <ArrowUp className="ml-2 h-4 w-4" />
            : <ArrowDown className="ml-2 h-4 w-4" />;
    }, [sortColumn, sortDirection]);

    // Combine into unified list
    const unifiedDocuments = useMemo(() => {
        const items: UnifiedDocumentItem[] = [];

        // Add anamnese responses
        anamneseResponses?.forEach((r: { id: number; createdAt: string }) => items.push({
            id: `anamnese-${r.id}`,
            name: `Anamnesebogen #${r.id}`,
            type: 'ANAMNESE',
            createdAt: new Date(r.createdAt),
            questionnaireResponseId: r.id,
        }));

        // Add informed consents
        informedConsents?.forEach((ic) => items.push({
            id: `consent-${ic.id}`,
            name: ic.templateName || `Aufklärung #${ic.id}`,
            type: 'AERZTLICHE_AUFKLAERUNG',
            createdAt: new Date(ic.createdAt),
            documentId: ic.documentId,
        }));

        // Add documents (split by type)
        documents?.forEach((doc) => {
            const type: UnifiedDocumentType = doc.type === 'BLOOD_REPORT' ? 'BLUTBEFUND' : 'SONSTIGES';
            items.push({
                id: `doc-${doc.id}`,
                name: doc.name,
                type,
                createdAt: new Date(doc.createdAt),
                documentId: doc.id,
            });
        });

        // Add blood reports
        bloodReports?.forEach((br) => items.push({
            id: `bloodreport-${br.id}`,
            name: br.documentDate
                ? `Blutreport vom ${format(new Date(br.documentDate), 'dd.MM.yyyy', { locale: de })}`
                : `Blutreport #${br.id}`,
            type: 'BLUTREPORT',
            createdAt: new Date(br.createdAt),
            bloodReportId: br.id,
            status: br.status,
            openHealthUrl: br.openHealthUrl,
        }));

        return items;
    }, [anamneseResponses, informedConsents, documents, bloodReports]);

    // Apply filtering and sorting
    const filteredAndSortedDocuments = useMemo(() => {
        let result = [...unifiedDocuments];

        // Apply type filter
        if (typeFilter !== 'ALL') {
            result = result.filter(doc => doc.type === typeFilter);
        }

        // Apply date filter
        if (dateFrom) {
            const fromStart = new Date(dateFrom);
            fromStart.setHours(0, 0, 0, 0);
            result = result.filter(doc => doc.createdAt >= fromStart);
        }
        if (dateTo) {
            const toEnd = new Date(dateTo);
            toEnd.setHours(23, 59, 59, 999);
            result = result.filter(doc => doc.createdAt <= toEnd);
        }

        // Apply sorting
        result.sort((a, b) => {
            let comparison = 0;
            switch (sortColumn) {
                case 'name':
                    comparison = a.name.localeCompare(b.name, 'de');
                    break;
                case 'type':
                    comparison = getUnifiedDocumentTypeLabel(a.type).localeCompare(getUnifiedDocumentTypeLabel(b.type), 'de');
                    break;
                case 'createdAt':
                    comparison = a.createdAt.getTime() - b.createdAt.getTime();
                    break;
            }
            return sortDirection === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [unifiedDocuments, typeFilter, dateFrom, dateTo, sortColumn, sortDirection]);

    const clearFilters = useCallback(() => {
        setTypeFilter('ALL');
        setDateFrom(undefined);
        setDateTo(undefined);
    }, []);

    const hasActiveFilters = typeFilter !== 'ALL' || dateFrom !== undefined || dateTo !== undefined;

    if (isLoading) {
        return (
            <div className="flex flex-col gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
            </div>
        );
    }

    if (unifiedDocuments.length === 0) {
        return (
            <div className="text-muted-foreground text-sm py-4">
                Keine Dokumente vorhanden.
            </div>
        );
    }

    return (
        <div className="w-full space-y-4">
            {/* Filter Controls */}
            <div className="flex flex-wrap gap-4 items-end">
                {/* Type Filter */}
                <div className="space-y-1">
                    <Label htmlFor="type-filter" className="text-sm">Typ</Label>
                    <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as UnifiedDocumentType | 'ALL')}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Alle Typen" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">Alle Typen</SelectItem>
                            {ALL_DOCUMENT_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                    {getUnifiedDocumentTypeLabel(type)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Date Range Filter */}
                <div className="space-y-1">
                    <Label className="text-sm">Zeitraum</Label>
                    <Popover
                        open={dateRangeOpen}
                        onOpenChange={(open) => {
                            setDateRangeOpen(open);
                            if (open) {
                                // Reset selection state when opening
                                setIsSelectingToDate(false);
                            }
                        }}
                    >
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    "w-[240px] justify-start text-left font-normal",
                                    !dateFrom && !dateTo && "text-muted-foreground"
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateFrom && dateTo ? (
                                    `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ${format(dateTo, 'dd.MM.yyyy', { locale: de })}`
                                ) : dateFrom ? (
                                    `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ...`
                                ) : (
                                    "Zeitraum wählen"
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <div className="p-3 border-b h-[52px] flex flex-col justify-center">
                                <p className="text-sm text-muted-foreground">
                                    {isSelectingToDate ? "Wähle das Enddatum" : "Wähle das Startdatum"}
                                </p>
                                <p className="text-xs text-muted-foreground h-4">
                                    {dateFrom && isSelectingToDate
                                        ? `Start: ${format(dateFrom, 'dd. MMM yyyy', { locale: de })}`
                                        : '\u00A0'
                                    }
                                </p>
                            </div>
                            <Calendar
                                mode="range"
                                locale={de}
                                selected={
                                    isSelectingToDate
                                        ? (dateFrom ? { from: dateFrom, to: dateFrom } : undefined)
                                        : undefined
                                }
                                onDayClick={(day) => {
                                    if (!isSelectingToDate) {
                                        // First click: set from date
                                        setDateFrom(day);
                                        setDateTo(undefined);
                                        setIsSelectingToDate(true);
                                    } else {
                                        // Second click: set to date and close
                                        let finalFrom = dateFrom;
                                        let finalTo = day;

                                        // Swap if to date is before from date
                                        if (dateFrom && day < dateFrom) {
                                            finalFrom = day;
                                            finalTo = dateFrom;
                                        }

                                        setDateFrom(finalFrom);
                                        setDateTo(finalTo);
                                        setIsSelectingToDate(false);
                                        setDateRangeOpen(false);
                                    }
                                }}
                                numberOfMonths={2}
                                defaultMonth={dateFrom || new Date()}
                            />
                        </PopoverContent>
                    </Popover>
                </div>

                {/* Clear Filters Button */}
                {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10">
                        <X className="mr-2 h-4 w-4" />
                        Filter zurücksetzen
                    </Button>
                )}

                {/* Action Button */}
                {actionButton && (
                    <div className="ml-auto">
                        {actionButton}
                    </div>
                )}
            </div>


            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                <Button
                                    variant="ghost"
                                    onClick={() => handleSort('name')}
                                    className="h-8 px-2 -ml-2 hover:bg-transparent"
                                >
                                    Name
                                    {getSortIcon('name')}
                                </Button>
                            </TableHead>
                            <TableHead>
                                <Button
                                    variant="ghost"
                                    onClick={() => handleSort('type')}
                                    className="h-8 px-2 -ml-2 hover:bg-transparent"
                                >
                                    Typ
                                    {getSortIcon('type')}
                                </Button>
                            </TableHead>
                            <TableHead>
                                <Button
                                    variant="ghost"
                                    onClick={() => handleSort('createdAt')}
                                    className="h-8 px-2 -ml-2 hover:bg-transparent"
                                >
                                    Datum
                                    {getSortIcon('createdAt')}
                                </Button>
                            </TableHead>
                            <TableHead>Aktionen</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredAndSortedDocuments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                                    Keine Dokumente gefunden.
                                </TableCell>
                            </TableRow>
                        ) : filteredAndSortedDocuments.map((doc) => (
                            <TableRow key={doc.id}>
                                <TableCell className="font-medium max-w-[200px] truncate" title={doc.name}>{doc.name}</TableCell>
                                <TableCell>
                                    <Badge variant="outline">
                                        {getUnifiedDocumentTypeLabel(doc.type)}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {format(doc.createdAt, 'dd.MM.yyyy HH:mm', { locale: de })}
                                </TableCell>
                                <TableCell>
                                    <div className="flex gap-2">
                                        {/* Anamnese: View modal button */}
                                        {doc.type === 'ANAMNESE' && doc.questionnaireResponseId && (
                                            <QuestionnaireResponseModalButton
                                                questionnaireResponseId={doc.questionnaireResponseId}
                                                createdAt={doc.createdAt}
                                                size="sm"
                                            />
                                        )}

                                        {/* Aufklärung, Blutbefund, Sonstiges: Download button */}
                                        {(doc.type === 'AERZTLICHE_AUFKLAERUNG' || doc.type === 'BLUTBEFUND' || doc.type === 'SONSTIGES') && doc.documentId && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleDownload(doc.documentId!)}
                                                title="Herunterladen"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        )}

                                        {/* Blutreport: Flow status button */}
                                        {doc.type === 'BLUTREPORT' && doc.bloodReportId && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setFlowModalReportId(doc.bloodReportId!)}
                                                title="Status anzeigen"
                                            >
                                                <Activity className="h-4 w-4" />
                                            </Button>
                                        )}

                                        {/* Blutreport: View OpenHealth report when URL exists */}
                                        {doc.type === 'BLUTREPORT' && doc.bloodReportId && doc.openHealthUrl && (
                                            <ButtonViewBloodReport bloodReportId={doc.bloodReportId} />
                                        )}

                                        {/* Blutreport: Show status if not ready */}
                                        {doc.type === 'BLUTREPORT' && doc.status && doc.status !== 'READY' && (
                                            <Badge variant={doc.status === 'ERROR' ? 'destructive' : 'secondary'}>
                                                {doc.status === 'PENDING' ? 'In Bearbeitung' : doc.status === 'ERROR' ? 'Fehler' : doc.status}
                                            </Badge>
                                        )}

                                        {/* Sonstiges & Blutbefund: Delete button */}
                                        {(doc.type === 'SONSTIGES' || doc.type === 'BLUTBEFUND') && doc.documentId && (
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        title="Löschen"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Dokument löschen</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Sind Sie sicher, dass Sie das Dokument "{doc.name}" löschen möchten?
                                                            Diese Aktion kann nicht rückgängig gemacht werden.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => handleDelete(doc.documentId!)}
                                                            className="bg-destructive hover:bg-destructive/90"
                                                        >
                                                            Löschen
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {flowModalReportId && (
                <BloodReportFlowModal
                    bloodReportId={flowModalReportId}
                    isOpen={!!flowModalReportId}
                    onClose={() => setFlowModalReportId(null)}
                />
            )}
        </div>
    );
}

type CustomerBillingDraftsListProps = {
    customerId: number;
};

const billingDraftStatusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING_PAYMENT: { label: 'Ausstehend', variant: 'outline' },
    PAID: { label: 'Bezahlt', variant: 'default' },
    CANCELLED: { label: 'Storniert', variant: 'destructive' },
};

const billingDraftTypeLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    NORMAL: { label: 'Standard', variant: 'outline' },
    INFLUENCER: { label: 'Influencer', variant: 'secondary' },
    MARKETING: { label: 'Marketing', variant: 'secondary' },
    ACADEMY: { label: 'Academy', variant: 'secondary' },
    KULANZ: { label: 'Kulanz/Beschwerde', variant: 'secondary' },
    CANCELLATION_FEE: { label: 'Ausfallentschädigung', variant: 'secondary' },
};

function CustomerBillingDraftsList({ customerId }: CustomerBillingDraftsListProps) {
    const navigate = useNavigate();
    const { data: billingDrafts, isLoading, error, refetch } = trpc.billingDrafts.getByCustomer.useQuery({ customerId });

    const columns: ColumnDef<NonNullable<typeof billingDrafts>[number]>[] = [
        {
            accessorKey: 'billingDraftNumber',
            header: 'Entwurfsnr.',
            cell: ({ row }) => (
                <div className='font-mono text-sm'>{row.original.billingDraftNumber}</div>
            ),
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => row.original.store.name,
        },
        {
            accessorKey: 'billingDraftDate',
            header: 'Datum',
            cell: ({ row }) => format(new Date(row.original.billingDraftDate), 'dd.MM.yyyy', { locale: de }),
        },
        {
            accessorKey: 'type',
            header: 'Typ',
            cell: ({ row }) => {
                const typeInfo = billingDraftTypeLabels[row.original.type] || { label: row.original.type, variant: 'outline' as const };
                return <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>;
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const status = billingDraftStatusLabels[row.original.status];
                return <Badge variant={status?.variant ?? 'outline'}>{status?.label ?? row.original.status}</Badge>;
            },
        },
        {
            accessorKey: 'totalGrossDiscounted',
            header: 'Betrag',
            cell: ({ row }) => {
                const amount = Number(row.original.totalGrossDiscounted);
                return (
                    <div className={`font-medium ${amount < 0 ? 'text-red-600' : ''}`}>
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)}
                    </div>
                );
            },
        },
        {
            id: 'totalPaid',
            header: 'Zahlung',
            cell: ({ row }) => {
                if (row.original.type === 'INFLUENCER' || row.original.type === 'MARKETING' || row.original.type === 'KULANZ') {
                    return <div className='font-medium text-muted-foreground'>-</div>;
                }
                const totalPaidEur = row.original.totalPaidCents / 100;
                const totalGrossDiscounted = Number(row.original.totalGrossDiscounted);
                const isMatch = Math.abs(totalPaidEur - totalGrossDiscounted) < 0.01;
                return (
                    <div className={`font-medium ${isMatch ? 'text-green-600' : 'text-red-600'}`}>
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(totalPaidEur)}
                    </div>
                );
            },
        },
        {
            id: 'actions',
            cell: ({ row }) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant='ghost' className='h-8 w-8 p-0'>
                            <span className='sr-only'>Menü öffnen</span>
                            <MoreHorizontal className='h-4 w-4' />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                        <DropdownMenuItem onClick={() => navigate(`/billing-draft/${row.original.id}`, { state: { from: `/customer/${customerId}`, tab: 'finanzen' } })}>
                            <Eye className='mr-2 h-4 w-4' />
                            Anzeigen
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(row.original.billingDraftNumber);
                            toast.success('Entwurfsnummer kopiert');
                        }}>
                            <FileText className='mr-2 h-4 w-4' />
                            Entwurfsnr. kopieren
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ];

    const table = useReactTable({
        data: billingDrafts ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (isLoading) {
        return (
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Aufträge</h3>
                </div>
                <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold">Aufträge</h3>
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Fehler</AlertTitle>
                    <AlertDescription>
                        Aufträge konnten nicht geladen werden.
                        <Button variant="link" onClick={() => refetch()} className="ml-2 p-0">
                            Erneut versuchen
                        </Button>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Aufträge</h3>
                <Button
                    size="sm"
                    onClick={() => navigate(`/billing-draft/new?customerId=${customerId}`, { state: { from: `/customer/${customerId}`, tab: 'finanzen' } })}
                >
                    Neuer Auftrag
                </Button>
            </div>

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHead key={header.id}>
                                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow
                                key={row.id}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => navigate(`/billing-draft/${row.original.id}`, { state: { from: `/customer/${customerId}`, tab: 'finanzen' } })}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className='h-24 text-center'>
                                Keine Aufträge vorhanden.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

// --- CustomerPaymentsList Component ---

const paymentStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    COMPLETED: { label: 'Abgeschlossen', variant: 'default' },
    PENDING: { label: 'Ausstehend', variant: 'outline' },
    FAILED: { label: 'Fehlgeschlagen', variant: 'destructive' },
    REFUNDED: { label: 'Erstattet', variant: 'secondary' },
};

const paymentTypeConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    BILLING_DRAFT_PAYMENT: { label: 'Zahlung', variant: 'default' },
    BILLING_DRAFT_REFUND: { label: 'Rückzahlung', variant: 'destructive' },
};

type CustomerPaymentsListProps = {
    customerId: number;
};

function CustomerPaymentsList({ customerId }: CustomerPaymentsListProps) {
    const navigate = useNavigate();
    const { data, isLoading, error, refetch } = trpc.payments.getByCustomer.useQuery(
        { customerId, pageSize: 100 }
    );

    const payments = data?.data ?? [];

    const columns: ColumnDef<(typeof payments)[number]>[] = [
        {
            accessorKey: 'createdAt',
            header: 'Datum',
            cell: ({ row }) => {
                const date = new Date(row.original.createdAt);
                return (
                    <div>
                        <div>{format(date, 'dd.MM.yyyy', { locale: de })}</div>
                        <div className="text-sm text-muted-foreground">{format(date, 'HH:mm', { locale: de })}</div>
                    </div>
                );
            },
        },
        {
            id: 'billingDraft',
            header: 'Auftrag',
            cell: ({ row }) => {
                const billingDraft = row.original.billingDraft;
                if (!billingDraft) return <span className="text-muted-foreground">-</span>;
                return (
                    <div
                        className="cursor-pointer hover:underline font-mono text-sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/billing-draft/${billingDraft.id}`, { state: { from: `/customer/${customerId}`, tab: 'finanzen' } });
                        }}
                    >
                        {billingDraft.billingDraftNumber}
                    </div>
                );
            },
        },
        {
            accessorKey: 'paidAmountCents',
            header: 'Betrag',
            cell: ({ row }) => {
                const amount = row.original.paidAmountCents / 100;
                const isRefund = row.original.type === 'BILLING_DRAFT_REFUND';
                return (
                    <div className={`font-medium ${isRefund ? 'text-destructive' : ''}`}>
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)}
                    </div>
                );
            },
        },
        {
            accessorKey: 'type',
            header: 'Typ',
            cell: ({ row }) => {
                const typeInfo = paymentTypeConfig[row.original.type] || { label: row.original.type, variant: 'outline' as const };
                return <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>;
            },
        },
        {
            accessorKey: 'paymentMethod',
            header: 'Methode',
            cell: ({ row }) => {
                const method = row.original.paymentMethod;
                if (!method) return <span className="text-muted-foreground">-</span>;
                const config = paymentMethodConfig[method] || defaultPaymentMethod;
                const Icon = config.icon;
                return (
                    <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span>{config.label}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const status = paymentStatusConfig[row.original.status] || { label: row.original.status, variant: 'outline' as const };
                return <Badge variant={status.variant}>{status.label}</Badge>;
            },
        },
        {
            id: 'createdBy',
            header: 'Erstellt von',
            cell: ({ row }) => {
                const user = row.original.createdByUser;
                if (!user) return <span className="text-muted-foreground">System</span>;
                return <span>{user.firstName} {user.lastName}</span>;
            },
        },
        {
            id: 'actions',
            cell: ({ row }) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/payment/${row.original.id}`)}>
                            <Eye className="mr-2 h-4 w-4" />
                            Details anzeigen
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ];

    const table = useReactTable({
        data: payments,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (isLoading) {
        return (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold">Zahlungen</h3>
                <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold">Zahlungen</h3>
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Fehler</AlertTitle>
                    <AlertDescription>
                        Zahlungen konnten nicht geladen werden.
                        <Button variant="link" onClick={() => refetch()} className="ml-2 p-0">
                            Erneut versuchen
                        </Button>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold">Zahlungen</h3>

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHead key={header.id}>
                                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id} className="cursor-pointer hover:bg-muted/50 relative">
                                {row.getVisibleCells().map((cell, index) => (
                                    <TableCell key={cell.id}>
                                        {index === 0 && (
                                            <Link
                                                to={`/payment/${row.original.id}`}
                                                className="absolute inset-0 z-[1]"
                                                tabIndex={-1}
                                            />
                                        )}
                                        {cell.column.id === 'actions' || cell.column.id === 'billingDraft' ? (
                                            <div className="relative z-[2]">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </div>
                                        ) : (
                                            flexRender(cell.column.columnDef.cell, cell.getContext())
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className='h-24 text-center'>
                                Keine Zahlungen vorhanden.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

// --- CustomerInvoicesList Component ---

type CustomerInvoicesListProps = {
    customerId: number;
};

function CustomerInvoicesList({ customerId }: CustomerInvoicesListProps) {
    const navigate = useNavigate();
    const { data: invoices, isLoading, error, refetch } = trpc.invoices.getByCustomer.useQuery({ customerId });
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    const downloadMutation = trpc.invoices.getDownloadUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
            setDownloadingId(null);
        },
        onError: (error) => {
            toast.error(`Fehler beim Download: ${error.message}`);
            setDownloadingId(null);
        },
    });

    const handleDownload = (invoiceId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setDownloadingId(invoiceId);
        downloadMutation.mutate({ id: invoiceId });
    };

    const columns: ColumnDef<NonNullable<typeof invoices>[number]>[] = [
        {
            accessorKey: 'invoiceNumber',
            header: 'Rechnungsnr.',
            cell: ({ row }) => (
                <div
                    className='font-mono text-sm cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/invoice/${row.original.id}`);
                    }}
                >
                    {row.original.invoiceNumber}
                </div>
            ),
        },
        {
            id: 'billingDraft',
            header: 'Entwurfsnr.',
            cell: ({ row }) => (
                <div
                    className='font-mono text-sm cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/billing-draft/${row.original.billingDraftId}`, { state: { from: `/customer/${customerId}`, tab: 'finanzen' } });
                    }}
                >
                    {row.original.billingDraftNumber}
                </div>
            ),
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => row.original.store.name,
        },
        {
            accessorKey: 'invoiceDate',
            header: 'Datum',
            cell: ({ row }) => format(new Date(row.original.invoiceDate), 'dd.MM.yyyy', { locale: de }),
        },
        {
            accessorKey: 'type',
            header: 'Typ',
            cell: ({ row }) => {
                const typeInfo = invoiceTypeLabels[row.original.type];
                if (!typeInfo) {
                    return <Badge variant='outline'>{row.original.type}</Badge>;
                }
                return <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>;
            },
        },
        {
            accessorKey: 'totalGross',
            header: 'Betrag',
            cell: ({ row }) => {
                const amount = Number(row.original.totalGross);
                return (
                    <div className={`font-medium ${amount < 0 ? 'text-red-600' : ''}`}>
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)}
                    </div>
                );
            },
        },
        {
            id: 'download',
            header: '',
            cell: ({ row }) => (
                <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 w-8 p-0'
                    onClick={(e) => handleDownload(row.original.id, e)}
                    disabled={downloadingId === row.original.id}
                >
                    {downloadingId === row.original.id ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                        <Download className='h-4 w-4' />
                    )}
                </Button>
            ),
        },
    ];

    const table = useReactTable({
        data: invoices ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (isLoading) {
        return (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold">Rechnungen</h3>
                <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold">Rechnungen</h3>
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Fehler</AlertTitle>
                    <AlertDescription>
                        Rechnungen konnten nicht geladen werden.
                        <Button variant="link" onClick={() => refetch()} className="ml-2 p-0">
                            Erneut versuchen
                        </Button>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold">Rechnungen</h3>

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHead key={header.id}>
                                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow
                                key={row.id}
                                className='cursor-pointer hover:bg-muted/50'
                                onClick={() => navigate(`/invoice/${row.original.id}`)}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className='h-24 text-center'>
                                Keine Rechnungen vorhanden.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

// --- Finanzen Sub-Tabs Component ---
type FinanzenSubTabsProps = {
    customerId: number;
};

type FinanzenSubTab = 'auftraege' | 'rechnungen' | 'zahlungen' | 'bundles' | 'wertgutscheine';

function FinanzenSubTabs({ customerId }: FinanzenSubTabsProps) {
    const [activeTab, setActiveTab] = useState<FinanzenSubTab>('auftraege');

    const tabs: { value: FinanzenSubTab; label: string }[] = [
        { value: 'auftraege', label: 'Aufträge' },
        { value: 'rechnungen', label: 'Rechnungen' },
        { value: 'zahlungen', label: 'Zahlungen' },
        { value: 'bundles', label: 'Bundles' },
        { value: 'wertgutscheine', label: 'Wertgutscheine' },
    ];

    return (
        <Card className="mt-4">
            <CardContent className="pt-4">
                {/* Button Group Navigation */}
                <div className="flex justify-end mb-4">
                    <div className="inline-flex rounded-md shadow-sm" role="group">
                        {tabs.map((tab, index) => (
                            <Button
                                key={tab.value}
                                variant={activeTab === tab.value ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setActiveTab(tab.value)}
                                className={cn(
                                    "focus:z-10",
                                    index === 0 && "rounded-r-none",
                                    index === tabs.length - 1 && "rounded-l-none",
                                    index > 0 && index < tabs.length - 1 && "rounded-none",
                                    index > 0 && "-ml-px"
                                )}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'auftraege' && (
                    <CustomerBillingDraftsList customerId={customerId} />
                )}

                {activeTab === 'rechnungen' && (
                    <CustomerInvoicesList customerId={customerId} />
                )}

                {activeTab === 'zahlungen' && (
                    <CustomerPaymentsList customerId={customerId} />
                )}

                {activeTab === 'bundles' && (
                    <CustomerBundlesTable customerId={customerId} />
                )}

                {activeTab === 'wertgutscheine' && (
                    <CustomerGiftCardsTable customerId={customerId} />
                )}
            </CardContent>
        </Card>
    );
}

const customerFormSchema = z.object({
    praxismanagerId: z.string().regex(/^\d*$/, 'Praxismanager ID darf nur aus Zahlen bestehen.').optional(),
    firstName: z.string().min(1, 'First name cannot be empty.').max(255).optional(),
    lastName: z.string().min(1, 'Last name cannot be empty.').max(255).optional(),
    email: z.string()
        .max(255)
        .refine(
            (val) => val === '' || z.email().safeParse(val).success,
            { message: 'Invalid email format.' }
        )
        .optional(),
    phone: z.string().max(255).nullish(),
    alternativePhone: z.string().max(255).optional().nullable(),
    birthday: z.date().optional().nullable(),
    gender: z.nativeEnum(CustomerGender).optional(),
    street: z.string().max(255).optional().nullable(),
    streetNumber: z.string().max(255).optional().nullable(),
    zipCode: z.string().max(255).optional().nullable(),
    city: z.string().max(255).optional().nullable(),
    country: z.nativeEnum(CustomerCountry).optional(),
    note: z.string().optional().nullable(),
    optOut: z.boolean(),
    tagTypes: z.array(z.enum(CustomerTagType)).optional(),
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

const DisplayStaticData = ({ label, value, icon: Icon }: { label: string; value?: string | number | null; icon?: React.ElementType }) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    return (
        <div className="flex items-start space-x-2 py-2"> {/* Increased py */}
            {Icon && <Icon className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />}
            <span className="text-sm font-medium text-muted-foreground w-28 shrink-0">{label}:</span> {/* Increased width */}
            <span className="text-sm">{value}</span>
        </div>
    );
};

type Appointment = {
    id: string;
    status: AppointmentStatus;
    steps?: Array<{
        name?: string;
        dateFromLocal?: LocalDateTime;
        dateUntilLocal?: LocalDateTime;
        practitioners: {
            practitioner: {
                id: number;
                title: string | null;
                firstName: string;
                lastName: string;
            }
        }[];
    }>;
    originalTreatmentVariants?: Array<{ treatmentVariant?: { name?: string | null } | null }>;
    titleShort?: string | null;
    title?: string | null;
    bookedPrice?: number | string | object | null; // Handle Decimal object potentially
    store: {
        name: string;
    } | null
};

const AppointmentList = ({ appointments }: { appointments: Appointment[] }) => {
    const {
        setAllPractitioners,
        setAllRooms,
        setAllEquipment,
        setSelectedStore,
        setCreateOrModifyAppointmentDrawerOpen,
    } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            setAllPractitioners: state.setAllPractitioners,
            setAllRooms: state.setAllRooms,
            setAllEquipment: state.setAllEquipment,
            setSelectedStore: state.setSelectedStore,
            setCreateOrModifyAppointmentDrawerOpen: state.setCreateOrModifyAppointmentDrawerOpen,
        }))
    );

    const { setMode, setAppointmentId, setNotifyCustomer } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            setMode: state.setMode,
            setAppointmentId: state.setAppointmentId,
            setNotifyCustomer: state.setNotifyCustomer,
        }))
    );


    // Fetch resources with less frequent updates (every 5 minutes)
    const {
        data: resourcesData,
        isLoading: isLoadingResources,
        refetch: refetchResources,
    } = trpc.appointments.getResources.useQuery(undefined, {
        refetchInterval: 300000, // 5 minutes
        refetchOnWindowFocus: true,
        staleTime: 300000,
    });

    // Update store with resources when they change
    useEffect(() => {
        if (resourcesData) {
            console.log("resourcesData")
            console.log(resourcesData)
            // Set all practitioners in the store
            if (resourcesData.practitioners && resourcesData.practitioners.length > 0) {
                setAllPractitioners(resourcesData.practitioners.map((practitioner) => ({
                    ...practitioner,
                    storeIds: practitioner.practitioner_store_practitioner_store_practitionerTopractitioner.map((assoc) => assoc.store_practitioner_store_storeTostore.id)
                })));
            }

            // Set all rooms in the store
            if (resourcesData.rooms && resourcesData.rooms.length > 0) {
                setAllRooms(resourcesData.rooms);
            }

            // Set all equipment in the store
            if (resourcesData.equipment && resourcesData.equipment.length > 0) {
                setAllEquipment(resourcesData.equipment);
            }
        }
    }, [resourcesData]);

    const trpcUtils = trpc.useUtils();

    const handleClickRow = async (appointmentId: string) => {
        try {
            const appointmentStore = await trpcUtils.appointments.getAppointmentStore.fetch({
                appointmentId: appointmentId,
            });
            setSelectedStore({
                id: appointmentStore.id,
                label: appointmentStore.name,
            })
            console.log("Found store for appointment:");
            console.log(appointmentStore);
        } catch (e) {
            console.log("Could not find appointment store");
            console.log(e);
        }
        setMode('modify');
        setAppointmentId(appointmentId);
        setNotifyCustomer(false);
        setCreateOrModifyAppointmentDrawerOpen(true);
    }

    if (!appointments || appointments.length === 0) {
        // Return null or a specific message if you want to handle the "No appointments found" message
        // in the parent component as done in the previous step. Returning null avoids double messages.
        return null;
        // return <p className="text-sm text-muted-foreground italic py-4">No appointments found.</p>;
    }

    const formatDateRange = (start?: LocalDateTime, end?: LocalDateTime): string => {
        if (!start) return 'Date N/A';

        const startDateStr = start.format('dd MMM yy'); // Shorter format maybe?
        const startTimeStr = start.format('HH:mm');
        const endTimeStr = !!end ? end.format('HH:mm') : '';
        return `${startDateStr}, ${startTimeStr}${endTimeStr ? ` - ${endTimeStr}` : ''}`;
    }

    return (
        // *** ADD table-fixed and w-full HERE ***
        <Table className="table-fixed w-full">
            <TableHeader>
                <TableRow>
                    {/* These widths are now more strictly enforced by table-fixed */}
                    <TableHead className="w-[110px]">Datum & Zeit</TableHead>
                    <TableHead className="w-[110px]">Behandlung</TableHead> {/* Flexible width column */}
                    <TableHead className="w-[140px]">Behandler</TableHead>
                    <TableHead className="w-[100px]">Clinic</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {appointments.map((app) => {
                    const sortedSteps = app.steps?.sort((a, b) => (a.dateUntilLocal?.toUTC().getTime() || 0) - (b.dateUntilLocal?.toUTC().getTime() || 0)) || [];

                    const earliestStart = sortedSteps[0].dateFromLocal;
                    const latestEnd = sortedSteps[sortedSteps.length - 1]?.dateUntilLocal;
                    const treatmentNames = app.originalTreatmentVariants
                        ?.map((v) => v?.treatmentVariant?.name)
                        .filter(Boolean)
                        .join(' + ');
                    const displayTitle = treatmentNames || app.title || 'Appointment';

                    const practitionersMap = new Map<number, string>();
                    for (const step of sortedSteps) {
                        for (const { practitioner } of step.practitioners ?? []) {
                            const titlePrefix = practitioner.title && practitioner.title.length > 0 ? `${practitioner.title} ` : '';
                            practitionersMap.set(practitioner.id, `${titlePrefix}${practitioner.firstName} ${practitioner.lastName}`);
                        }
                    }
                    const practitionersList = Array.from(practitionersMap.values());

                    return (
                        <TableRow key={app.id} className='cursor-pointer' onClick={() => handleClickRow(app.id)}>
                            {/* Keep nowrap on date, table-fixed handles overflow better */}
                            <TableCell className="font-medium whitespace-pre-wrap py-2 align-top">
                                {formatDateRange(earliestStart, latestEnd)}
                            </TableCell>
                            {/* break-words is still useful here for long strings within the cell */}
                            <TableCell className="py-2 align-top wrap-anywhere whitespace-pre-wrap">
                                {displayTitle}
                            </TableCell>
                            <TableCell className="py-2 align-top">
                                {practitionersList.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                        {practitionersList.map((name) => (
                                            <Badge key={`appointment-${app.id}-practitioner-${name}`} variant='outline' className="whitespace-pre-wrap">
                                                {name}
                                            </Badge>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-xs text-muted-foreground">–</span>
                                )}
                            </TableCell>
                            <TableCell className="py-2 align-top">
                                <Badge variant='outline'>
                                    {app.store?.name || 'Digital'}
                                </Badge>
                            </TableCell>
                            <TableCell className="py-2 align-top">
                                <Badge variant={app.status === 'COMPLETED' ? 'default' : ['CANCELLED', 'NO_SHOW', 'DECLINED', 'CALLED_OFF'].includes(app.status) ? 'destructive' : 'outline'}>
                                    {app.status || 'N/A'}
                                </Badge>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

type SkinReportsListProps = {
    customerId: number;
}
const SkinReportsList = ({ customerId }: SkinReportsListProps) => {
    const skinReportsQuery = trpc.customers.getCustomerSimpleSkinReports.useQuery({
        id: customerId,
    });
    const skinReportsData = useMemo(() => {
        return skinReportsQuery.data;
    }, [skinReportsQuery]);

    const myLoginTokenQuery = trpc.user.getMyLoginToken.useQuery();

    // Loading state
    if (skinReportsQuery.isLoading) {
        return (
            <div className="space-y-4 p-4">
                {[1, 2, 3].map((item) => (
                    <div key={item} className="space-y-3">
                        <div className="flex items-center gap-4">
                            <Skeleton className="h-16 w-16 rounded-lg" />
                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <Skeleton className="h-6 w-32" />
                                    <Skeleton className="h-4 w-20" />
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {[1, 2, 3, 4].map((metric) => (
                                <Skeleton key={metric} className="h-20 w-full" />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Error state
    if (skinReportsQuery.isError) {
        return (
            <Alert variant="destructive" className="m-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                    Fehler beim Laden der Hautberichte. Bitte versuchen Sie es später erneut.
                </AlertDescription>
            </Alert>
        );
    }

    // No data state
    if (!skinReportsData || skinReportsData.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center">
                <Activity className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">
                    Keine Hautanalyse für diesen Kunden gefunden.
                </p>
            </div>
        );
    }

    const metricConfig = {
        numberForeheadLines: { label: "Stirnfalten", color: "default" },
        numberFrownLines: { label: "Zornesfalten", color: "default" },
        numberCrowsFeet: { label: "Krähenfüße", color: "default" },
        numberSpots: { label: "Flecken", color: "secondary" },
        numberBrownSpots: { label: "Braune Flecken", color: "secondary" },
        numberUltravioletSpots: { label: "UV-Flecken", color: "destructive" },
        numberPorphyrine: { label: "Porphyrin", color: "outline" },
        numberPores: { label: "Poren", color: "outline" },
    };

    return (
        <div className="space-y-6 py-4">
            {skinReportsData.map((report, index) => (
                <div
                    key={report.id}
                    className="space-y-4 pb-6 border-b last:border-0"
                >
                    <div className="flex items-start gap-4">
                        {/* Rest of the content */}
                        <div className="flex-1 space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Badge variant="secondary" className="text-xs">
                                        Hautanalyse #{skinReportsData.length - index}
                                    </Badge>
                                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                        <CalendarIcon className="h-3 w-3" />
                                        {/* The scanner reports Beijing wall-clock time (UTC+8, no DST) as if it were UTC */}
                                        {format(new Date(new Date(report.diagnosisTime).getTime() - 8 * 60 * 60 * 1000), "dd.MM.yyyy")}
                                    </div>
                                </div>
                                <div className='flex flex-row items-center gap-2'>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="group"
                                        onClick={() => {
                                            const baseUrl = import.meta.env.MODE === 'production'
                                                ? 'https://treatmentplanner.kalialab.de'
                                                : import.meta.env.MODE === 'staging'
                                                    ? 'https://staging.treatmentplanner.kalialab.de'
                                                    : 'http://localhost:3000';
                                            window.open(`${baseUrl}/dashboard/skin-reports/${report.id}${!!myLoginTokenQuery?.data ? `?user_token=${myLoginTokenQuery.data}` : ""}`, "_blank");
                                        }}
                                    >
                                        <span className="mr-1 text-sm">Im Behandlungsplaner öffnen</span>
                                        <ExternalLink className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="group"
                                        onClick={() => window.open(report.url, "_blank")}
                                    >
                                        <span className="mr-1 text-sm">Skin Report öffnen</span>
                                        <ExternalLink className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                                    </Button>
                                </div>
                            </div>

                            <div className='w-full flex flex-row gap-4'>
                                {/* Small image on the left */}
                                <div className="shrink-0">
                                    <img
                                        src={report.imageSrc}
                                        alt={`Skin report ${skinReportsData.length - index}`}
                                        className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg object-cover border"
                                    />
                                </div>
                                {/* <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {(Object.entries(report.metrics) as [keyof typeof metricConfig, number][]).map(
                                        ([metric, value]) => {
                                            const config = metricConfig[metric];
                                            if (!config || value === 0) return null;
                                            return (
                                                <div
                                                    key={metric}
                                                    className="bg-card border rounded-lg p-3 space-y-1"
                                                >
                                                    <p className="text-xs text-muted-foreground">
                                                        {config.label}
                                                    </p>
                                                    <p className="text-2xl font-semibold tabular-nums">
                                                        {value}
                                                    </p>
                                                </div>
                                            );
                                        }
                                    )}
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

type ButtonCreateInformedConsentProps = {
    customerId: number;
}
const ButtonCreateInformedConsent = ({ customerId }: ButtonCreateInformedConsentProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedStore, setSelectedStore] = useState<{ id: number; name: string } | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<{ id: string; name: string } | null>(null);
    const [selectedPractitioner, setSelectedPractitioner] = useState<{ id: number; firstName: string; lastName: string } | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const trpcUtils = trpc.useUtils();
    const generateInformedConsentUrlMutation = trpc.customers.generateInformedConsentUrl.useMutation();

    const allStoresQuery = trpc.stores.getAll.useQuery({ pageSize: 1000 }, { enabled: isDialogOpen });
    const allStores = useMemo(() => allStoresQuery.data?.data ?? [], [allStoresQuery.data]);

    const allPractitionersQuery = trpc.practitioners.getAll.useQuery(
        { pageSize: 1000, filters: { storeId: selectedStore?.id?.toString() } },
        { enabled: !!selectedStore }
    );

    const allPractitioners = useMemo(() => {
        if (!allPractitionersQuery.data?.data) return [];
        const doctorCategoryId = 1;
        const cosmeticianCategoryId = 2;

        if (selectedTemplate?.id && TEMPLATES_ALLOWED_FOR_COSMETICIANS.includes(selectedTemplate.id)) {
            return allPractitionersQuery.data.data.filter(
                (p) => p.categoryId === doctorCategoryId || p.categoryId === cosmeticianCategoryId
            );
        }
        return allPractitionersQuery.data.data.filter((p) => p.categoryId === doctorCategoryId);
    }, [allPractitionersQuery.data, selectedTemplate]);

    const resetForm = useCallback(() => {
        setSelectedStore(null);
        setSelectedTemplate(null);
        setSelectedPractitioner(null);
        setStatus('idle');
        setErrorMessage('');
    }, []);

    const handleDialogOpenChange = (open: boolean) => {
        setIsDialogOpen(open);
        if (!open) {
            resetForm();
        }
    };

    const handleCreate = async () => {
        if (!selectedStore || !selectedTemplate || !selectedPractitioner) return;

        setIsCreating(true);
        setStatus('idle');

        try {
            const link = await generateInformedConsentUrlMutation.mutateAsync({
                customerId,
                practitionerId: selectedPractitioner.id,
                storeId: selectedStore.id,
                templateId: selectedTemplate.id,
            });

            setStatus('success');
            window.open(link, '_blank');

            setTimeout(() => {
                setIsDialogOpen(false);
                resetForm();
                trpcUtils.customers.listInformedConsents.refetch();
            }, 1000);
        } catch (error) {
            setStatus('error');
            setErrorMessage(error instanceof Error ? error.message : 'Fehler beim Erstellen der Aufklärung');
        } finally {
            setIsCreating(false);
        }
    };

    const canCreate = selectedStore && selectedTemplate && selectedPractitioner && !isCreating;

    return (
        <>
            <Button
                variant="ghost"
                size="default"
                className="w-full sm:w-auto"
                onClick={() => setIsDialogOpen(true)}
            >
                <FileText className="h-4 w-4 mr-2" />
                Aufklärung erstellen
            </Button>

            <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogContent className="sm:max-w-md max-w-[calc(100vw-2rem)] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Aufklärung erstellen
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 overflow-hidden">
                        <div className="space-y-2">
                            <Label>Standort</Label>
                            <Select
                                value={selectedStore?.id?.toString() || ''}
                                onValueChange={(value) => {
                                    const store = allStores.find((s) => s.id.toString() === value);
                                    setSelectedStore(store ? { id: store.id, name: store.name } : null);
                                    setSelectedPractitioner(null);
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Standort auswählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {allStores.map((store) => (
                                        <SelectItem key={store.id} value={store.id.toString()}>
                                            {store.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2 min-w-0">
                            <Label>Aufklärung Vorlage</Label>
                            <Select
                                value={selectedTemplate?.id || ''}
                                onValueChange={(value) => {
                                    const template = INFORMED_CONSENT_TEMPLATES[value];
                                    setSelectedTemplate(template || null);
                                    setSelectedPractitioner(null);
                                }}
                            >
                                <SelectTrigger className="w-full overflow-hidden">
                                    <span className="truncate block">
                                        {selectedTemplate?.name || "Vorlage auswählen"}
                                    </span>
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.values(INFORMED_CONSENT_TEMPLATES).map((template) => (
                                        <SelectItem key={template.id} value={template.id}>
                                            {template.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Behandler</Label>
                            <Select
                                value={selectedPractitioner?.id?.toString() || ''}
                                onValueChange={(value) => {
                                    const practitioner = allPractitioners.find((p) => p.id.toString() === value);
                                    setSelectedPractitioner(practitioner ? { id: practitioner.id, firstName: practitioner.firstName, lastName: practitioner.lastName } : null);
                                }}
                                disabled={!selectedStore || !selectedTemplate}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={!selectedStore ? "Erst Standort auswählen" : !selectedTemplate ? "Erst Vorlage auswählen" : "Behandler auswählen"} />
                                </SelectTrigger>
                                <SelectContent>
                                        {allPractitioners.map((practitioner) => (
                                            <SelectItem key={practitioner.id} value={practitioner.id.toString()}>
                                                <PractitionerNameWithCategory
                                                    firstName={practitioner.firstName}
                                                    lastName={practitioner.lastName}
                                                    title={practitioner.title}
                                                    categoryName={practitioner.practitioner_category?.name}
                                                />
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {status === 'success' && (
                            <Alert className="border-green-200 bg-green-50">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <AlertDescription className="text-green-800">
                                    Aufklärung wurde geöffnet!
                                </AlertDescription>
                            </Alert>
                        )}

                        {status === 'error' && (
                            <Alert className="border-red-200 bg-red-50">
                                <AlertCircle className="h-4 w-4 text-red-600" />
                                <AlertDescription className="text-red-800">
                                    {errorMessage}
                                </AlertDescription>
                            </Alert>
                        )}

                        <Button
                            onClick={handleCreate}
                            disabled={!canCreate}
                            className="w-full"
                        >
                            {isCreating ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Wird erstellt...
                                </>
                            ) : (
                                <>
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Aufklärung starten
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

type FetchedCustomer = customer & {
    createdAt: string | Date;
    updatedAt: string | Date;
    birthday: string | Date;
    deletedAt?: string | Date | null;
};

// --- Main Customer View Component ---
const CustomerView = () => {
    const params = useParams();
    const id = parseInt(params.id!, 10);
    const utils = trpc.useUtils();

    const userData = useUserStore();
    const isAdmin = userData.user?.role === UserRole.ADMIN;
    const canEditNewsletter = isAdmin || userData.user?.role === UserRole.OPERATOR;

    const navigate = useNavigate();
    const location = useLocation();

    // Restore the tab the user was on when navigating back from a sub-page (e.g. a billing draft)
    const [activeMainTab, setActiveMainTab] = useState<string>(() => (location.state as { tab?: string } | null)?.tab || 'uebersicht');

    // --- Data Fetching ---
    const { data: queryResult, isLoading: isLoadingCustomer, error: customerError, refetch: refetchCustomer } = trpc.customers.getOne.useQuery(
        { id },
        {
            enabled: !isNaN(id),
        }
    );

    const { data: latestAnamneseData } = trpc.customers.getLatestQuestionnaireResponse.useQuery(
        { customerId: id },
        { placeholderData: keepPreviousData }
    );

    // Customer balance query (total payments minus outstanding billing draft gross)
    const { data: balanceData } = trpc.customerMoney.getCustomerBalance.useQuery(
        { customerId: id },
        { enabled: !isNaN(id) }
    );
    const walletBalance = balanceData?.balanceEur ?? 0;

    // Payment history query for calculating total paid
    const { data: paymentsData } = trpc.payments.getByCustomer.useQuery(
        { customerId: id, pageSize: 1000 },
        { enabled: !isNaN(id) }
    );
    const totalPaidCents = useMemo(() => {
        if (!paymentsData?.data) return 0;
        return paymentsData.data.reduce((sum, payment) => sum + payment.paidAmountCents, 0);
    }, [paymentsData]);

    // State for payment/bundle dialogs
    const [showPaymentHistoryDialog, setShowPaymentHistoryDialog] = useState(false);
    const [showBundleVouchersDialog, setShowBundleVouchersDialog] = useState(false);

    // Fetch bundle vouchers for this customer
    const { data: bundleVouchersData } = trpc.customerMoney.getCustomerBundleVouchers.useQuery(
        { customerId: id },
        { enabled: !isNaN(id) }
    );
    const activeBundleVouchers = bundleVouchersData?.filter(v => v.remainingUnits > 0) ?? [];

    // Fetch gift cards for this customer
    const { data: giftCardsData } = trpc.giftCards.getByCustomer.useQuery(
        { customerId: id },
        { enabled: !isNaN(id) }
    );

    const totalGuthabenEur = useMemo(() => {
        const giftCardCredit = (giftCardsData?.data ?? [])
            .filter(gc => gc.remainingBalance > 0)
            .reduce((sum, gc) => sum + gc.remainingBalance, 0);

        const bundleCredit = (bundleVouchersData ?? [])
            .filter(v => v.remainingUnits > 0)
            .reduce((sum, v) => sum + v.remainingValueEur, 0);

        return giftCardCredit + bundleCredit;
    }, [giftCardsData, bundleVouchersData, id]);

    const { healthGroupItems, nonHealthGroupItems } = useMemo(() => {
        if (!latestAnamneseData) {
            return { healthGroupItems: [], nonHealthGroupItems: [] };
        }

        const parsedAnamneseItems = typeof latestAnamneseData.items === "string" ? JSON.parse(latestAnamneseData.items) : latestAnamneseData.items;
        const parsedQuestionnaire = parseFHIRQuestionnaire(JSON.parse(latestAnamneseData.questionnaire));
        const questionnaireCAVEItemLinkIds = getCaveItems(parsedQuestionnaire);

        type FilterFn = (linkId: string) => boolean;

        const filterItems = (filterFn: any) => {
            return parsedAnamneseItems.map((item: any) => ({
                ...item,
                item: item.item
                    ?.filter((answerItem: { linkId: string }) => filterFn(answerItem.linkId))
                    .map((answerItem: { answer?: any[];[key: string]: any }) => ({
                        ...answerItem,
                        answer: answerItem.answer?.map((answer: any) => {
                            let isBlank = null;
                            if (Object.keys(answer).length === 0) {
                                isBlank = true;
                            }

                            if (answer.valueString !== undefined && (answer.valueString.trim().length === 0)) {
                                isBlank = true;
                            } else if (answer.valueString !== undefined && answer.valueString.trim().length > 0) {
                                isBlank = false;
                            }

                            if (answer.valueBoolean === true) {
                                isBlank = false;
                            } else if (answer.valueBoolean === false && isBlank === null) {
                                isBlank = true;
                            }

                            if (answer.answerDecimal !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerInteger !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerTime !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerDate !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerDateTime !== undefined) {
                                isBlank = false;
                            }

                            if (answer.answerQuantity !== undefined) {
                                isBlank = false;
                            }

                            if (answer.valueCoding !== undefined) {
                                isBlank = false;
                            }

                            if (isBlank !== null) {
                                console.log(answer);
                            }

                            return {
                                ...answer,
                                isBlank: isBlank === null ? false : isBlank,
                            }
                        }),
                    })),
            })).filter((item: any) => item.item && item.item.length > 0);
        };

        const healthItems = filterItems((linkId: string) => questionnaireCAVEItemLinkIds.includes(linkId));
        const nonHealthItems = filterItems((linkId: string) => !questionnaireCAVEItemLinkIds.includes(linkId));

        console.log("healthItems");
        console.log(healthItems);

        return { healthGroupItems: healthItems, nonHealthGroupItems: nonHealthItems };
    }, [latestAnamneseData]);

    const affirmativeCaveFields = useMemo(() => {
        if (!healthGroupItems || healthGroupItems.length === 0) {
            return [];
        }

        type QuestionGroup = { // Question group
            text: string;
            item: { // Question
                text: string;
                answer: any[] | undefined;
            }[] | undefined;
        }

        const questions: any[] = healthGroupItems.flatMap((questionGroup: QuestionGroup) => questionGroup.item);

        const affirmativeData: { question: string; answers: string[]; }[] = [];

        for (const question of questions) {
            const answers: any[] = question.answer || [];
            for (const answer of answers) {
                const isAffirmative = getIsAffirmative(answer) || answer.isBlank === false;
                if (isAffirmative) {
                    const answerValue = getAnswerValue(answer);
                    const existingItem = affirmativeData.find((item) => item.question === question.text);
                    if (existingItem) {
                        existingItem.answers.push(answerValue)
                    } else {
                        affirmativeData.push({
                            question: question.text,
                            answers: [answerValue],
                        })
                    }
                }
            }
        }

        return affirmativeData;
    }, [healthGroupItems]);

    console.log("affirmativeCaveFields");
    console.log(healthGroupItems);
    console.log(affirmativeCaveFields);

    const customer = queryResult?.data;

    const processedCustomer = useMemo(() => {
        if (!queryResult?.data) return null;
        const rawCustomerData = queryResult.data;
        const processed = {
            ...rawCustomerData,
            gender: rawCustomerData.gender as CustomerGender,
            createdAt: new Date(rawCustomerData.createdAt),
            updatedAt: new Date(rawCustomerData.updatedAt),
            birthday: rawCustomerData.birthday ? new Date(rawCustomerData.birthday) : undefined,
            deletedAt: rawCustomerData.deletedAt ? new Date(rawCustomerData.deletedAt) : null,
        } as FetchedCustomer;
        return processed;
    }, [queryResult?.data]);

    // --- Form Setup ---
    const form = useForm<CustomerFormValues>({
        resolver: zodResolver(customerFormSchema),
        // Provide initial default values to prevent uncontrolled -> controlled switch
        defaultValues: {
            praxismanagerId: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            alternativePhone: '',
            birthday: null,
            gender: CustomerGender.UNKNOWN,
            street: '',
            streetNumber: '',
            zipCode: '',
            city: '',
            country: CustomerCountry.GERMANY,
            note: '',
            optOut: false,
            tagTypes: [],
        },
    });
    const { formState: { isDirty }, reset, handleSubmit, control } = form;

    // --- Update Mutation ---
    const mutation = trpc.customers.update.useMutation({
        onSuccess: (updatedData) => {
            toast.success("Customer Updated", {
                description: `${updatedData?.data?.firstName} ${updatedData?.data?.lastName}'s details saved.`,
            });
            // Invalidate cache to refetch fresh data
            utils.customers.getOne.invalidate({ id });
            utils.customers.getAll.invalidate(); // Invalidate list view if needed

            // Reset the form with the *new* data and reset dirty state
            // Need to handle potential date string conversion if API returns strings
            const resetData = {
                ...updatedData.data,
                birthday: updatedData?.data?.birthday ? new Date(updatedData?.data?.birthday) : null,
                praxismanagerId: String(updatedData?.data?.praxismanagerId),
            };
            reset(resetData as CustomerFormValues);

            // Optionally trigger parent callback if needed
            // onUpdateSuccess?.();
        },
        onError: (error) => {
            toast.error("Update Failed", {
                description: error.message || "Could not update customer details.",
            });
        },
    });

    const transformPrismaCustomerToFormData = (customer: FetchedCustomer | null | undefined): Partial<CustomerFormValues> => {
        if (!customer) return {};
        const formData = {
            praxismanagerId: !!customer.praxismanagerId ? String(customer.praxismanagerId) : '',
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email || '',
            phone: customer.phone,
            alternativePhone: customer.alternativePhone,
            birthday: customer.birthday,
            gender: customer.gender || CustomerGender.UNKNOWN,
            street: customer.street,
            streetNumber: customer.streetNumber,
            zipCode: customer.zipCode,
            city: customer.city,
            country: customer.country,
            note: customer.note,
            optOut: customer.optOut ?? false,
        };

        console.log("Transforming Prisma Customer to FormData:", formData.gender);

        return formData;
    };

    useEffect(() => {
        if (processedCustomer) {
            // Map fetched data, ensuring consistent types (null for optional fields)
            console.log("processedCustomer")
            console.log(processedCustomer)
            const formDataToReset = transformPrismaCustomerToFormData(processedCustomer);
            form.reset(formDataToReset);
        }
    }, [processedCustomer, form.reset]);

    // --- Form Submit Handler ---
    function onSubmit(_values: CustomerFormValues) {
        if (!customer) return; // Should not happen if button is enabled correctly
        console.log("Submitting form values:", _values);
        const { birthday, praxismanagerId, email, optOut, ...values } = _values;
        const emailValue = !email ? null : email.trim().length === 0 ? null : email.toLowerCase();
        const praxismanagerIdStrValue = !praxismanagerId ? null : praxismanagerId.trim().length === 0 ? null : Number(praxismanagerId);
        mutation.mutate({ id: customer.id, email: emailValue, birthday: !!birthday ? format(birthday, 'yyyy-MM-dd') : undefined, praxismanagerId: isAdmin ? praxismanagerIdStrValue : undefined, ...(canEditNewsletter ? { optOut } : {}), ...values });
    }

    // --- Memoized Static Data ---
    const formattedCreatedAt = useMemo(() => {
        return customer?.createdAt ? format(new Date(customer.createdAt), 'dd.MM.yyyy, HH:mm') : 'N/A';
    }, [customer?.createdAt]);

    const formattedUpdatedAt = useMemo(() => {
        return customer?.updatedAt ? format(new Date(customer.updatedAt), 'dd.MM.yyyy, HH:mm') : 'N/A';
    }, [customer?.updatedAt]);

    const customerStoreNames: string[] = useMemo(() => {
        if (!customer) {
            return [];
        }
        const allCustomerAppointments = [...customer.pastAppointments, ...customer.upcomingAppointments];
        if (allCustomerAppointments.length === 0) {
            return [];
        }

        const storeNames = new Set<string>();

        allCustomerAppointments.forEach((appointment) => {
            const store = appointment.store?.name;
            if (!store) {
                return;
            }
            if (!storeNames.has(store)) {
                storeNames.add(store);
            }
        })

        return Array.from(storeNames);
    }, [customer]);

    const customerAge = !customer ? 'N/A' : customer.birthday
        ? `${Math.floor(
            (Date.now() - new Date(customer.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365.25)
        )}`
        : 'N/A';

    const birthdayInfo = useMemo(() => {
        if (!customer?.birthday) return null;
        const birthday = new Date(customer.birthday);
        const today = new Date();
        const thisYearBirthday = new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate());
        const diffTime = today.getTime() - thisYearBirthday.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays >= 0 && diffDays <= 7) {
            return { daysAgo: diffDays };
        }
        return null;
    }, [customer?.birthday]);

    // --- Render Logic ---
    if (isNaN(id)) {
        return <div className="p-4 text-red-600">Invalid Customer ID provided.</div>;
    }

    if (isLoadingCustomer) {
        return (
            <div className="flex items-center justify-center h-60">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
        );
    }

    if (customerError || !customer) {
        return (
            <Alert variant="destructive" className="m-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error Fetching Customer</AlertTitle>
                <AlertDescription>
                    {customerError?.message || 'Could not load customer data. The customer might not exist.'}
                    <Button variant="link" onClick={() => refetchCustomer()} className="p-0 h-auto ml-2">Retry</Button>
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <React.Fragment>
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                {/* --- Header (Now includes Save/Discard Buttons) --- */}
                <div className="flex flex-col flex-wrap items-start justify-start mb-6 gap-4">
                    <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
                        <div
                            className='flex justify-center items-center p-2 hover:bg-gray-200 transition-all cursor-pointer'
                            onClick={() => {
                                navigate("/customers");
                            }}
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </div>
                        <User className="h-6 w-6" />
                        {customer.firstName} {customer.lastName}, {customerAge} J.

                        {parseInt(customerAge) < 18 && customerAge !== 'N/A' && (
                            <div className="relative flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-red-600">
                                <span className="text-red-600 text-base font-bold">18</span>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-full h-px bg-black rotate-45 origin-center"></div>
                                </div>
                            </div>
                        )}

                        {birthdayInfo && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="flex flex-col items-center">
                                        <Cake className="h-5 w-5 text-pink-500" />
                                        <span className="text-[9px] leading-tight text-pink-500 font-medium">
                                            {birthdayInfo.daysAgo === 0 ? 'Heute' : `vor ${birthdayInfo.daysAgo}d`}
                                        </span>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" align="center">
                                    {birthdayInfo.daysAgo === 0
                                        ? 'Hat heute Geburtstag! 🎂'
                                        : `Hatte vor ${birthdayInfo.daysAgo} ${birthdayInfo.daysAgo === 1 ? 'Tag' : 'Tagen'} Geburtstag`}
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </h1>

                    <Card className="w-full">
                        <CardContent className="py-0">
                            <div className='flex flex-row flex-wrap gap-x-6 gap-y-1 text-xs'>
                                <div className='flex flex-row gap-1 items-center'>
                                    <span className='text-muted-foreground'>Alter:</span>
                                    <span className='font-medium'>{customerAge} J.</span>
                                </div>
                                <div className='flex flex-row gap-1 items-center'>
                                    <span className='text-muted-foreground'>Customer ID:</span>
                                    <span className='font-medium'>{customer.id ?? 'N/A'}</span>
                                </div>
                                <div className='flex flex-row gap-1 items-center'>
                                    <span className='text-muted-foreground'>Praxismanager ID:</span>
                                    {!!customer.praxismanagerId ? (
                                        <a
                                            href={`https://10.31.1.5/index.php?module=customer&index[customer][action]=details&index[customer][data][customer_id]=${customer.praxismanagerId}`}
                                            target='_blank'
                                            className='font-medium underline text-primary-400'>{customer.praxismanagerId}
                                        </a>
                                    ) : (
                                        <span className='font-medium'>{customer.praxismanagerId ?? 'N/A'}</span>
                                    )}
                                </div>
                                <div className='flex flex-row gap-1 items-center'>
                                    <span className='text-muted-foreground'>Geschlecht:</span>
                                    <span className='font-medium'>{customer.gender ?? 'N/A'}</span>
                                </div>
                                <div className='flex flex-row gap-1 items-center'>
                                    <span className='text-muted-foreground'>Patient möchte Empfehlungen/Newsletter erhalten:</span>
                                    <span className='font-medium'>{customer.optOut ? 'Nein' : 'Ja'}</span>
                                </div>
                                {customer.note && (
                                    <div className='flex flex-row gap-1 items-start basis-full'>
                                        <span className='text-muted-foreground'>Notiz:</span>
                                        <span className='font-medium text-yellow-700'>{customer.note}</span>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {latestAnamneseData && (
                    <div className='grid grid-cols-3 gap-2 my-2'>
                        {affirmativeCaveFields.map((item) => (
                            <Alert key={item.question + '-' + item.answers} variant="destructive" className="py-2 px-3">
                                <AlertCircleIcon className="h-3 w-3" />
                                <AlertTitle className="text-xs">CAVE</AlertTitle>
                                <AlertDescription className="text-xs">
                                    <p className="font-medium">{item.question}</p>
                                    <ul className="list-inside list-disc text-sm">
                                        {item.answers.map((answer, idx) => (
                                            <li key={`cave-field-${item.question}-answer-${idx}`}>
                                                {answer}
                                            </li>
                                        ))}
                                    </ul>
                                </AlertDescription>
                            </Alert>
                        ))}
                        <div className='self-center'>
                            <QuestionnaireResponseModalButton
                                questionnaireResponseId={latestAnamneseData.id}
                                createdAt={new Date(latestAnamneseData.createdAt)}
                            />
                        </div>
                    </div>
                )}

                {/* --- Main Content (Single Full-Width Column) --- */}
                <Form {...form}>
                    <Tabs value={activeMainTab} onValueChange={setActiveMainTab}>
                        <TabsList>
                            <TabsTrigger value="uebersicht" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Übersicht</TabsTrigger>
                            <TabsTrigger value="stamm-daten" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Stamm-Daten</TabsTrigger>
                            <TabsTrigger value="patientenakte" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Dokumente</TabsTrigger>
                            <TabsTrigger value="blutproben" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Labor-Aufträge</TabsTrigger>
                            <TabsTrigger value="finanzen" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Finanzen</TabsTrigger>
                            <TabsTrigger value="dokumentation" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Dokumentation</TabsTrigger>
                        </TabsList>

                        {/* === ÜBERSICHT TAB === */}
                        <TabsContent value="uebersicht" className="space-y-4">
                            {/* --- Action Buttons --- */}
                            <div className="pt-2 flex flex-wrap gap-2">
                                <ButtonOpenTreatmentPlanner customerId={customer.id} />
                                <ButtonCreateInformedConsent customerId={customer.id} />
                                <SendAnamneseUrlDialog
                                    customerId={customer.id}
                                    triggerVariant="ghost"
                                    triggerSize="default"
                                    triggerClassName="w-full sm:w-auto"
                                />
                            </div>

                            {/* --- Termine Section --- */}
                            <Card>
                                <CardHeader className="py-3">
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <CalendarIconLucide className="h-4 w-4" />
                                        Terminhistorie
                                    </CardTitle>
                                    {customerStoreNames.length > 0 && (
                                        <CardDescription className="text-xs mt-0 mb-0">
                                            <div className='flex flex-row items-center gap-1'>
                                                <span className="text-muted-foreground">Bisherige Standorte:</span>
                                                {customerStoreNames.map((store, idx) => (
                                                    <Badge key={idx} variant='outline' className="text-xs py-0">
                                                        {store}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </CardDescription>
                                    )}
                                </CardHeader>
                                <CardContent className="space-y-3 overflow-y-auto max-h-[500px] pr-3 py-2">
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">
                                            Bevorstehende ({customer.upcomingAppointments?.length ?? 0})
                                        </h4>
                                        <AppointmentList appointments={!!customer.upcomingAppointments ? customer.upcomingAppointments.map((appointment) => ({
                                            ...appointment,
                                            steps: appointment.steps.map((step) => ({
                                                ...step,
                                                dateFromLocal: LocalDateTime.deserialize(step.dateFromLocalSerialized),
                                                dateUntilLocal: LocalDateTime.deserialize(step.dateUntilLocalSerialized),
                                            }))
                                        })) : []} />
                                        {(customer.upcomingAppointments?.length ?? 0) === 0 && (customer.pastAppointments?.length ?? 0) > 0 && (
                                            <p className="text-xs text-muted-foreground italic py-2">Keine bevorstehenden Termine verfügbar.</p>
                                        )}
                                    </div>

                                    {(customer.upcomingAppointments?.length ?? 0) > 0 && (customer.pastAppointments?.length ?? 0) > 0 && (
                                        <Separator className="my-2" />
                                    )}

                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">
                                            Vergangene ({customer.pastAppointments?.length ?? 0})
                                        </h4>
                                        <AppointmentList appointments={!!customer.pastAppointments ? customer.pastAppointments.map((appointment) => ({
                                            ...appointment,
                                            steps: appointment.steps.map((step) => ({
                                                ...step,
                                                dateFromLocal: LocalDateTime.deserialize(step.dateFromLocalSerialized),
                                                dateUntilLocal: LocalDateTime.deserialize(step.dateUntilLocalSerialized),
                                            }))
                                        })) : []} />
                                    </div>

                                    {(customer.upcomingAppointments?.length ?? 0) === 0 && (customer.pastAppointments?.length ?? 0) === 0 && (
                                        <p className="text-xs text-muted-foreground italic py-2 text-center">Keine Termine für diesen Kunden verfügbar.</p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* --- Hautanalysen Section --- */}
                            <Card>
                                <CardHeader className="py-3">
                                    <CardTitle className="text-base">Hautanalysen</CardTitle>
                                </CardHeader>
                                <CardContent className="py-2">
                                    <SkinReportsList customerId={id} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* === STAMM-DATEN TAB === */}
                        <TabsContent value="stamm-daten" className="space-y-3">
                            {/* Customer Form */}
                            <Card>
                                <CardContent className="pt-4">
                                    <form onSubmit={handleSubmit(onSubmit)} className='space-y-3'>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                            {/* Left Column: Contact Info & Note */}
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <FormField control={control} name="praxismanagerId" render={({ field }) => (<FormItem hidden={!isAdmin} className="space-y-1"><FormLabel className="text-xs">Praxismanager ID</FormLabel><FormControl><Input {...field} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="firstName" render={({ field }) => (<FormItem className="space-y-1"><FormLabel className="text-xs">Vorname</FormLabel><FormControl><Input {...field} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="lastName" render={({ field }) => (<FormItem className="space-y-1"><FormLabel className="text-xs">Nachname</FormLabel><FormControl><Input {...field} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="email" render={({ field }) => (<FormItem className="space-y-1"><FormLabel className="text-xs">E-Mail</FormLabel><FormControl><Input type="email" {...field} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="phone" render={({ field }) => (<FormItem className="space-y-1"><FormLabel className="text-xs">Telefonnummer</FormLabel><FormControl><Input {...field} value={field.value ?? ''} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="alternativePhone" render={({ field }) => (<FormItem className="space-y-1"><FormLabel className="text-xs">Alt. Telefon</FormLabel><FormControl><Input {...field} value={field.value ?? ''} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="birthday" render={({ field }) => (
                                                        <FormItem className="flex flex-col space-y-1">
                                                            <FormLabel className="text-xs">Geburtsdatum</FormLabel>
                                                            <Popover>
                                                                <PopoverTrigger asChild>
                                                                    <FormControl>
                                                                        <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal h-8 text-sm", !field.value && "text-muted-foreground")}>
                                                                            {field.value ? format(field.value, 'dd.MM.yyyy', { locale: de }) : <span>Datum auswählen</span>}
                                                                            <CalendarIconLucide className="ml-auto h-3 w-3 opacity-50" />
                                                                        </Button>
                                                                    </FormControl>
                                                                </PopoverTrigger>
                                                                <PopoverContent className="w-auto p-0" align="start">
                                                                    <Calendar
                                                                        mode="single"
                                                                        locale={de}
                                                                        selected={field.value ?? undefined}
                                                                        onSelect={(date) => field.onChange(date || null)}
                                                                        disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                                                                        autoFocus
                                                                        captionLayout="dropdown"
                                                                        startMonth={new Date(1900, 0)}
                                                                        endMonth={new Date(new Date().getFullYear() - 18, new Date().getMonth())}
                                                                    />
                                                                </PopoverContent>
                                                            </Popover>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )} />
                                                    <FormField
                                                        control={form.control}
                                                        name="gender"
                                                        render={({ field }) => (
                                                            <FormItem className="space-y-1">
                                                                <FormLabel className="text-xs">Geschlecht</FormLabel>
                                                                <Select onValueChange={field.onChange} value={field.value} key={`gender-${field.value}`}>
                                                                    <FormControl>
                                                                        <SelectTrigger className="h-8">
                                                                            <SelectValue placeholder="Auswählen" />
                                                                        </SelectTrigger>
                                                                    </FormControl>
                                                                    <SelectContent>
                                                                        {Object.values(CustomerGender).map((value) => (
                                                                            <SelectItem key={value} value={value}>{value}</SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                    <FormField
                                                        control={form.control}
                                                        name="optOut"
                                                        render={({ field }) => (
                                                            <FormItem className="col-span-2 space-y-1">
                                                                <FormLabel className="text-xs">Patient möchte Empfehlungen/Newsletter erhalten</FormLabel>
                                                                <Select
                                                                    onValueChange={(value) => field.onChange(value === 'no')}
                                                                    value={field.value ? 'no' : 'yes'}
                                                                    disabled={!canEditNewsletter}
                                                                    key={`newsletter-${field.value}`}
                                                                >
                                                                    <FormControl>
                                                                        <SelectTrigger className="h-8">
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                    </FormControl>
                                                                    <SelectContent>
                                                                        <SelectItem value="yes">Ja</SelectItem>
                                                                        <SelectItem value="no">Nein</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>

                                                {/* Internal Note */}
                                                <FormField control={control} name="note" render={({ field }) => (
                                                    <FormItem className="space-y-1">
                                                        <FormLabel className="flex items-center gap-1 text-xs"><FileText className="h-3 w-3 text-muted-foreground" />Interne Notiz</FormLabel>
                                                        <FormControl><Textarea className="min-h-[60px] text-sm" {...field} value={field.value ?? ''} /></FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )} />
                                            </div>

                                            {/* Right Column: Address */}
                                            <div className="space-y-2 lg:border-l lg:border-gray-200 lg:pl-4">
                                                <h4 className="text-xs font-medium flex items-center gap-1"><MapPin className="h-3 w-3 text-muted-foreground" /> Adresse</h4>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <FormField control={control} name="street" render={({ field }) => (<FormItem className="col-span-2 space-y-1"><FormLabel className="text-xs">Straße</FormLabel><FormControl><Input {...field} value={field.value ?? ''} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="streetNumber" render={({ field }) => (<FormItem className="space-y-1"><FormLabel className="text-xs">Hausnummer</FormLabel><FormControl><Input {...field} value={field.value ?? ''} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="zipCode" render={({ field }) => (<FormItem className="space-y-1"><FormLabel className="text-xs">PLZ</FormLabel><FormControl><Input {...field} value={field.value ?? ''} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField control={control} name="city" render={({ field }) => (<FormItem className="space-y-1"><FormLabel className="text-xs">Stadt</FormLabel><FormControl><Input {...field} value={field.value ?? ''} className="h-8" /></FormControl><FormMessage /></FormItem>)} />
                                                    <FormField
                                                        control={form.control}
                                                        name="country"
                                                        render={({ field }) => (
                                                            <FormItem className="space-y-1">
                                                                <FormLabel className="text-xs">Land</FormLabel>
                                                                <Select onValueChange={field.onChange} value={field.value} key={`country-${field.value}`}>
                                                                    <FormControl>
                                                                        <SelectTrigger className="h-8">
                                                                            <SelectValue placeholder="Auswählen" />
                                                                        </SelectTrigger>
                                                                    </FormControl>
                                                                    <SelectContent>
                                                                        {Object.values(CustomerCountry).map((countryValue) => (
                                                                            <SelectItem key={countryValue} value={countryValue}>{countryValue.replace('_', ' ')}</SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Meta Info & Actions */}
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2 border-t">
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <CalendarCheck className="h-3 w-3" />
                                                <span>Kunde seit:</span>
                                                <span className="font-medium text-foreground">{formattedCreatedAt}</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <RefreshCw className="h-3 w-3" />
                                                <span>Letzte Änderung:</span>
                                                <span className="font-medium text-foreground">{formattedUpdatedAt}</span>
                                            </div>
                                            <div className="flex items-center gap-2 ml-auto">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    type='button'
                                                    onClick={() => reset()}
                                                    disabled={mutation.isPending || !isDirty}
                                                    className="h-7 text-xs"
                                                >
                                                    <X className="mr-1 h-3 w-3" />
                                                    Verwerfen
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    type='button'
                                                    onClick={handleSubmit(onSubmit)}
                                                    disabled={mutation.isPending || !isDirty}
                                                    className="h-7 text-xs"
                                                >
                                                    {mutation.isPending ? (
                                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <Save className="mr-1 h-3 w-3" />
                                                    )}
                                                    Speichern
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* === PATIENTENAKTE TAB === */}
                        <TabsContent value="patientenakte" className="space-y-4">
                            <Card>
                                <CardContent className="overflow-y-auto max-h-[500px] pr-3 pt-4 pb-2">
                                    <UnifiedPatientDocumentsTable
                                        customerId={customer.id}
                                        actionButton={<CustomerDocumentUploader customerId={customer.id} />}
                                    />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* === BLUTPROBEN TAB === */}
                        <TabsContent value="blutproben">
                            <Tabs defaultValue="aniva" className="mt-4">
                                <TabsList>
                                    <TabsTrigger value="aniva">Aniva Health</TabsTrigger>
                                    {isAdmin && <TabsTrigger value="remi">Remi Health</TabsTrigger>}
                                </TabsList>
                                <TabsContent value="aniva">
                                    <div className="space-y-4 mt-4">
                                        <h3 className="text-lg font-semibold mb-2">Aniva Health - Labor-Aufträge</h3>
                                        <AnivaLabOrderCreator customerId={customer.id} />
                                        <Separator />
                                        <h4 className="text-md font-medium">Bestehende Aniva-Aufträge</h4>
                                        <AnivaLabOrdersList customerId={customer.id} />
                                    </div>
                                </TabsContent>
                                {isAdmin && (
                                    <TabsContent value="remi">
                                        <div className="space-y-4 mt-4">
                                            <h3 className="text-lg font-semibold mb-2">Remi Health - Bestehende Labor-Aufträge</h3>
                                            <LabOrdersList customerId={customer.id} />
                                        </div>
                                    </TabsContent>
                                )}
                            </Tabs>
                        </TabsContent>

                        {/* === DOKUMENTATION TAB === */}
                        <TabsContent value="dokumentation">
                            <MedicalRecordSection customerId={customer.id} customerNote={customer?.note} />
                        </TabsContent>

                        {/* === FINANZEN TAB === */}
                        <TabsContent value="finanzen">
                            {/* Financial Summary */}
                            <Card className="mb-4">
                                <CardContent className="py-2">
                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                                        {/* Total Payments */}
                                        <div className="flex items-center gap-2">
                                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm text-muted-foreground">Gesamt bezahlt:</span>
                                            <span className="text-sm font-semibold">
                                                {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(totalPaidCents / 100)}
                                            </span>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-1"
                                                onClick={() => setShowPaymentHistoryDialog(true)}
                                            >
                                                <History className="h-3 w-3" />
                                            </Button>
                                        </div>

                                        {/* Customer Balance */}
                                        <div className="flex items-center gap-2">
                                            <Wallet className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm text-muted-foreground">Saldo:</span>
                                            <span className={`text-sm font-semibold ${walletBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(walletBalance)}
                                            </span>
                                        </div>

                                        {/* Customer Guthaben */}
                                        <div className="flex items-center gap-2">
                                            <Gift className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm text-muted-foreground">Guthaben:</span>
                                            <span className={`text-sm font-semibold ${totalGuthabenEur > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                                {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(totalGuthabenEur)}
                                            </span>
                                        </div>

                                        {/* Bundle Vouchers */}
                                        <div className="flex items-center gap-2">
                                            <Gift className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm text-muted-foreground">Bundles:</span>
                                            <span className="text-sm font-semibold">
                                                {activeBundleVouchers.length} aktiv
                                            </span>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-1"
                                                onClick={() => setShowBundleVouchersDialog(true)}
                                            >
                                                <Eye className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Finanzen Sub-Navigation */}
                            <FinanzenSubTabs customerId={customer.id} />
                        </TabsContent>
                    </Tabs>
                </Form>
            </div>


            <CreateOrModifyAppointmentDrawer
                onSubmit={() => {
                    refetchCustomer();
                    toast.success("Termin gespeichert");
                }}
            />

            <PaymentHistoryDialog
                open={showPaymentHistoryDialog}
                onOpenChange={setShowPaymentHistoryDialog}
                customerId={id}
            />

            <BundleVouchersDialog
                open={showBundleVouchersDialog}
                onOpenChange={setShowBundleVouchersDialog}
                customerId={id}
                isAdmin={isAdmin}
            />
        </React.Fragment>
    );
};

export default CustomerView;